<template>
  <v-card class="fill-height" style="margin-top: 130px" dark>
    <PersonalRecordAnimation :visible.sync="showPRAnimation" :record-type="prAnimationData.recordType" :new-value="prAnimationData.newValue" />

    <v-card-text class="exercise-scroll-area pa-0" :style="{ 'padding-top': initialHeight + 10 + 'px' }">
      <template v-if="visibleWarmupExercises.length">
        <div class="section-header mt-4 mb-2 px-4 d-flex align-center">
          <v-icon small color="orange" class="mr-2">mdi-fire</v-icon>
          <span class="header-text orange--text text--lighten-1">AUFWÄRMEN</span>
          <v-divider class="ml-3" style="border-color: rgba(255, 165, 0, 0.3)"></v-divider>
        </div>

        <div class="px-3">
          <div
            v-for="exercise in visibleWarmupExercises"
            :key="exercise._id + '-warmup'"
            class="exercise-wrapper mb-3"
            v-touch="{
              left: () => skipExercise(exercise._id),
              right: () => {},
            }">
            <div
              class="modern-card"
              :class="{
                'is-completed': isExerciseCompleted(exercise),
                'is-active': selectedExerciseIndex === exercise.originalIndex,
              }"
              @click.stop="open(exercise, $event)"
              v-ripple>
              <div class="card-content d-flex align-center py-3 pr-2 pl-3">
                <div class="warmup-indicator mr-3">
                  <div class="warmup-dot" :class="{ filled: isExerciseCompleted(exercise) }">
                    <v-icon v-if="isExerciseCompleted(exercise)" x-small color="white">mdi-check</v-icon>
                  </div>
                </div>

                <div class="image-box mr-3">
                  <v-img :src="getImageForExercise(exercise)" class="exercise-img" />
                </div>

                <div class="flex-grow-1 overflow-hidden">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <div class="exercise-title text-truncate">{{ exercise.title }}</div>

                    <v-btn icon x-small color="grey darken-2" @click.stop="skipExercise(exercise._id)" class="skip-btn">
                      <v-icon small>mdi-eye-off-outline</v-icon>
                    </v-btn>
                  </div>

                  <div class="exercise-meta">
                    <div v-if="exercise.title === 'Senso Pro'" class="d-flex align-center">
                      <span class="caption grey--text">{{ isExerciseCompleted(exercise) ? "Erledigt" : "Durchgang offen" }}</span>
                    </div>
                    <div v-else class="d-flex align-center">
                      <span class="caption font-weight-bold orange--text">Warmup</span>
                      <span class="caption grey--text ml-2" v-if="!isExerciseCompleted(exercise)">{{ exercise.completedSetCount }} / 1</span>
                      <v-icon v-if="isExerciseCompleted(exercise)" x-small color="green" class="ml-2">mdi-check</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="visibleRegularExercises.length">
        <div class="section-header mt-6 mb-2 px-4 d-flex align-center" v-if="visibleWarmupExercises.length">
          <v-icon small color="#00E676" class="mr-2">mdi-dumbbell</v-icon>
          <span class="header-text green--text text--accent-3">WORKOUT</span>
          <v-divider class="ml-3" style="border-color: rgba(0, 230, 118, 0.3)"></v-divider>
        </div>

        <div class="px-3 pb-16">
          <template v-for="(exercise, index) in visibleRegularExercises">
            <div v-if="!exercise.isSuperset" :key="exercise._id + index + '-regular'" class="exercise-wrapper mb-3">
              <div
                class="modern-card"
                :class="{
                  'is-completed': isExerciseCompleted(exercise),
                  'is-active': selectedExerciseIndex === exercise.originalIndex,
                  'hyrox-mode': activeSplit.isHyrox,
                }"
                @click.stop="open(exercise, $event)"
                v-ripple>
                <div
                  class="status-border"
                  :class="{
                    done: isExerciseCompleted(exercise),
                    hyrox: activeSplit.isHyrox,
                  }"></div>

                <div class="card-content d-flex align-center py-3 pr-2 pl-4">
                  <div class="image-box mr-3">
                    <v-img :src="getImageForExercise(exercise)" class="exercise-img" />
                    <div v-if="isExerciseCompleted(exercise)" class="completed-overlay">
                      <v-icon color="white">mdi-check-bold</v-icon>
                    </div>
                  </div>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="d-flex align-center overflow-hidden">
                        <div class="exercise-title text-truncate mr-2">{{ exercise.title }}</div>

                        <v-chip
                          v-if="exercise.method && intensityTechniqueMapping[exercise.method]"
                          :color="intensityTechniqueMapping[exercise.method].color"
                          x-small
                          label
                          class="px-2 method-chip font-weight-bold black--text"
                          style="background: transparent !important; height: 20px; border: 1px solid white">
                          <v-icon x-small left :color="intensityTechniqueMapping[exercise.method].color">{{
                            intensityTechniqueMapping[exercise.method].icon
                          }}</v-icon>
                          {{ intensityTechniqueMapping[exercise.method].short }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="exercise-meta">
                      <template v-if="exercise.title === 'Senso Pro'">
                        <span :class="isExerciseCompleted(exercise) ? 'green--text' : 'grey--text'">
                          {{ isExerciseCompleted(exercise) ? "Abgeschlossen" : "Offen" }}
                        </span>
                      </template>
                      <template v-else>
                        <div class="d-flex align-center justify-space-between pr-2">
                          <div class="progress-pills">
                            <div
                              v-if="exercise.warmup"
                              class="pill"
                              :class="{
                                'filled-warmup': exercise.isWarmupCompleted,
                                'outlined-warmup': !exercise.isWarmupCompleted,
                              }"></div>
                            <div
                              v-for="n in exercise.plannedSetCount"
                              :key="n"
                              class="pill"
                              :class="{
                                filled: n <= exercise.completedSetCount,
                                'filled-hyrox': activeSplit.isHyrox && n <= exercise.completedSetCount,
                              }"></div>
                          </div>
                          <span class="caption grey--text text--lighten-1 ml-2" style="min-width: 40px; text-align: right">
                            {{ exercise.completedSetCount }}/{{ exercise.plannedSetCount }}
                          </span>
                        </div>
                      </template>
                    </div>
                  </div>

                  <div class="action-icon ml-1" @click.stop>
                    <v-menu bottom left offset-y content-class="premium-context-menu" transition="slide-y-transition" rounded="xl">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="grey darken-1" v-bind="attrs" v-on="on" large>
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense class="glass-menu-list pa-2">
                        <v-subheader
                          class="text-uppercase caption font-weight-bold grey--text text--lighten-1 mb-1 pl-2"
                          style="letter-spacing: 1px; height: 32px">
                          Optionen
                        </v-subheader>

                        <v-list-item @click="$emit('edit', exercise)" class="menu-item-rounded mb-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="blue lighten-1">mdi-pencil</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium white--text">Bearbeiten</v-list-item-title>
                        </v-list-item>

                        <div class="my-1" style="border-top: 1px solid rgba(255, 255, 255, 0.1)"></div>

                        <v-list-item @click="$emit('move', { exercise, direction: 'up' })" :disabled="index === 0" class="menu-item-rounded mb-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="orange lighten-1">mdi-arrow-up</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium white--text">Nach oben</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          @click="$emit('move', { exercise, direction: 'down' })"
                          :disabled="index === visibleRegularExercises.length - 1"
                          class="menu-item-rounded mb-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="orange lighten-1">mdi-arrow-down</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium white--text">Nach unten</v-list-item-title>
                        </v-list-item>

                        <div class="my-1" style="border-top: 1px solid rgba(255, 255, 255, 0.1)"></div>

                        <v-list-item @click="skipExercise(exercise._id)" class="menu-item-rounded mb-1">
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="red lighten-1">mdi-eye-off-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium white--text">Ausblenden</v-list-item-title>
                        </v-list-item>

                        <v-list-item @click="$emit('delete', exercise)" class="menu-item-rounded">
                          <v-list-item-icon class="mr-3">
                            <v-icon small color="red lighten-1">mdi-delete-outline</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title class="font-weight-medium white--text">Entfernen</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </div>
              </div>
            </div>

            <div v-else :key="exercise.supersetId + '-superset'" class="exercise-wrapper mb-4">
              <div
                class="modern-card superset-container"
                :class="{ 'is-completed': isExerciseCompleted(exercise) }"
                @click.stop="open(exercise, $event)"
                v-ripple>
                <div class="superset-line-container">
                  <div class="superset-line"></div>
                  <div class="superset-icon"><v-icon x-small color="black">mdi-link</v-icon></div>
                </div>

                <div class="superset-content pl-4 pt-2 pr-2">
                  <div class="d-flex justify-space-between align-center pr-2">
                    <div class="superset-label mb-1">SUPERSATZ</div>
                    <v-btn icon x-small color="grey darken-2" @click.stop="skipExercise(exercise.exercises[0]._id)" class="skip-btn">
                      <v-icon small>mdi-eye-off-outline</v-icon>
                    </v-btn>
                  </div>

                  <div
                    v-for="(subEx, subIndex) in exercise.exercises"
                    :key="subEx._id"
                    class="superset-item d-flex align-center py-2 pr-2"
                    :class="{ 'mb-1': subIndex < exercise.exercises.length - 1 }">
                    <div class="image-box small mr-3">
                      <v-img :src="getImageForExercise(subEx)" class="exercise-img" />
                      <div
                        v-if="getCompletedSetsForSubExercise(exercise, subEx).length >= (subEx.sets ? subEx.sets.length : 0)"
                        class="completed-overlay">
                        <v-icon color="white" x-small>mdi-check</v-icon>
                      </div>
                    </div>

                    <div class="flex-grow-1">
                      <div class="exercise-title-small">{{ subEx.title }}</div>
                      <div class="d-flex align-center mt-1">
                        <div class="progress-pills small">
                          <div
                            v-for="n in subEx.sets ? subEx.sets.length : 0"
                            :key="n"
                            class="pill"
                            :class="{ filled: n <= getCompletedSetsForSubExercise(exercise, subEx).length }"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div v-if="skippedExercises.length > 0" class="text-center mt-4">
            <v-btn text small color="grey darken-1" @click="skippedExercises = []">
              <v-icon left small>mdi-eye</v-icon> {{ skippedExercises.length }} ausgeblendete Übungen anzeigen
            </v-btn>
          </div>

          <div class="mt-8 mb-4 px-2">
            <div class="d-flex mb-3">
              <v-btn
                block
                large
                rounded
                text
                class="font-weight-bold"
                @click="promptAutoFinish"
                style="letter-spacing: 0.5px; color: rgba(0, 150, 63, 1)">
                <v-icon left small>mdi-auto-fix</v-icon>
                Alle eintragen & Beenden
              </v-btn>
            </div>

            <v-btn
              block
              large
              rounded
              color="#2C2C2E"
              class="finish-btn elevation-0"
              @click="promptFinishWorkout"
              style="border: 1px solid rgba(255, 255, 255, 0.1)">
              <span class="white--text font-weight-bold mr-2">Training abschliessen</span>
              <v-icon color="white" small>mdi-flag-checkered</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </v-card-text>
    <v-dialog v-model="showWeightPropagationDialog" max-width="450px" dark persistent content-class="elevation-0" overlay-opacity="0.8">
      <v-card v-if="propagationData" class="rounded-xl pa-3 glass-card-dialog propagation-dialog-card">
        <v-card-title class="font-weight-bold justify-center pb-1">
          <v-icon left :color="propagationData.isIncrease ? 'green accent-3' : 'orange'">
            {{ propagationData.isIncrease ? "mdi-trending-up" : "mdi-trending-down" }}
          </v-icon>
          Plan-Update Vorschlag
        </v-card-title>

        <v-card-text class="white--text mt-4">
          <template v-if="propagationData.type === 'absolute'">
            <v-row align="center" justify="center" no-gutters class="text-center">
              <v-col>
                <div class="text-caption grey--text">Bisheriger Plan</div>
                <div class="weight-box old-weight">{{ propagationData.oldValue }}</div>
              </v-col>
              <v-col cols="auto" class="px-2">
                <v-icon size="32" class="arrow-anim" color="grey lighten-1">mdi-arrow-right-bold</v-icon>
              </v-col>
              <v-col>
                <div class="text-caption" :class="propagationData.isIncrease ? 'green--text text--lighten-2' : 'orange--text text--lighten-2'">
                  Dein neuer Plan
                </div>
                <div class="weight-box new-weight" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                  {{ propagationData.newValue }}
                </div>
              </v-col>
            </v-row>
            <p class="text-center mt-6 body-1">{{ propagationData.description }}</p>
          </template>

          <template v-else>
            <div class="text-center">
              <div class="text-caption grey--text">Bisheriger Plan (nächste Sätze)</div>
              <div class="weight-box old-weight mt-1">
                <span v-for="(val, i) in propagationData.oldValue" :key="`old-${i}`" class="mx-2">{{ val }} kg</span>
              </div>

              <div class="connector-wrapper">
                <div class="operation-indicator" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                  <v-icon>{{ propagationData.isIncrease ? "mdi-plus" : "mdi-minus" }}</v-icon>
                  <span>{{ propagationData.newValue.replace("+", "") }} kg</span>
                </div>
              </div>

              <div class="text-caption" :class="propagationData.isIncrease ? 'green--text text--lighten-2' : 'orange--text text--lighten-2'">
                Dein neuer Plan
              </div>
              <div class="weight-box new-weight mt-1" :class="{ increase: propagationData.isIncrease, decrease: !propagationData.isIncrease }">
                <span
                  v-for="(val, i) in propagationData.newValueCalculated"
                  :key="`new-${i}`"
                  class="new-weight-value mx-2"
                  :style="{ 'animation-delay': `${i * 100}ms` }">
                  {{ val }} kg
                </span>
              </div>

              <p class="text-center mt-5 body-2 grey--text text--lighten-1">{{ propagationData.subDescription }}</p>
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0 d-block text-center">
          <v-btn :color="propagationData.isIncrease ? 'green' : 'orange'" depressed rounded large block class="mb-2" @click="executePropagation">
            <v-icon left>mdi-check-all</v-icon>
            Alle Sätze anpassen
          </v-btn>
          <v-btn text small color="grey" @click="closePropagationDialog"> Nein, nur diesen Satz </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showIncompleteDialog" max-width="450px" dark persistent content-class="elevation-0" overlay-opacity="0.8">
      <v-card class="incomplete-dialog-card text-center pa-5">
        <div class="incomplete-dialog-bg"></div>
        <v-icon size="100" color="orange darken-1" class="alert-icon mb-4">mdi-alert-decagram-outline</v-icon>
        <h2 class="font-weight-bold text-h4 white--text mb-3">Training unvollständig</h2>
        <p class="white--text text--lighten-1 mb-6">Du hast noch nicht alle geplanten Sätze abgeschlossen. Folgende Übungen sind noch offen:</p>

        <v-list dense color="transparent" class="py-0 mb-6 text-left incomplete-list">
          <v-list-item v-for="ex in incompleteExercises" :key="ex.title" class="px-1 mb-2">
            <v-list-item-icon class="mr-4 my-auto">
              <v-icon color="grey lighten-1">mdi-weight-lifter</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold white--text">{{ ex.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ ex.completed }} von {{ ex.planned }} Sätzen erledigt</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-row dense>
          <v-col cols="12">
            <v-btn large rounded block color="orange darken-2" class="font-weight-bold" @click="showIncompleteDialog = false"> Fortsetzen </v-btn>
          </v-col>
          <v-col cols="12" class="pt-2">
            <v-btn large rounded block text @click="finishAnyway" class="font-weight-bold grey--text">Trotzdem beenden</v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAutoFinishDialog" fullscreen dark persistent>
      <div style="position: relative; background: #121212; height: 100vh; display: flex; flex-direction: column">
        <div class="auto-finish-bg-glow" :style="{ background: `radial-gradient(circle, ${splitColor}1a 0%, transparent 70%)` }"></div>

        <!-- Header -->
        <div class="pa-6 pb-2 text-center relative" style="z-index: 1">
          <div
            class="d-inline-flex align-center justify-center pa-4 rounded-circle mb-3"
            :style="{ background: `${splitColor}15`, boxShadow: `0 0 20px ${splitColor}22` }">
            <v-icon size="40" :color="splitColor">mdi-auto-fix</v-icon>
          </div>
          <h3 class="font-weight-black text-h5 white--text mb-1" style="letter-spacing: 0.5px">Auto-Complete</h3>
          <p class="grey--text text--lighten-1 text-body-2 mb-0">Überprüfe die Werte für deine offenen Sätze.</p>
        </div>

        <!-- Scrollable Content -->
        <div class="pa-4 overflow-y-auto custom-scrollbar relative" style="flex: 1; z-index: 1">
          <div v-if="autoCompletePreviewData.length === 0" class="text-center py-10 grey--text">
            <v-icon size="48" color="grey darken-2" class="mb-2">mdi-check-all</v-icon>
            <br />Keine offenen Sätze gefunden.
          </div>

          <!-- Exercise Loop -->
          <v-card
            v-for="(exercise, i) in autoCompletePreviewData"
            :key="i"
            class="mb-4 rounded-xl pa-4"
            style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05); box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2)">
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <div class="avatar-icon-box mr-3" :style="{ background: `${splitColor}15`, border: `1px solid ${splitColor}33` }">
                  <v-icon small :color="splitColor">mdi-dumbbell</v-icon>
                </div>
                <span class="font-weight-bold white--text text-subtitle-1">{{ exercise.exerciseTitle }}</span>
              </div>
              <v-btn icon small color="grey lighten-1" @click="removeExercise(i)">
                <v-icon small>mdi-close-circle-outline</v-icon>
              </v-btn>
            </div>

            <!-- Table Header -->
            <div
              v-if="exercise.sets && exercise.sets.length > 0"
              class="d-flex align-center px-2 py-1 mb-1 caption grey--text font-weight-bold text-uppercase"
              style="letter-spacing: 1px; font-size: 0.7rem !important">
              <div style="width: 28px; text-align: center">Satz</div>
              <div v-if="!exercise.sets[0].isDurationOnly" class="flex-grow-1 mx-1 text-center" style="width: 40%">Gewicht</div>
              <div v-if="!exercise.sets[0].isDurationOnly" class="flex-grow-1 mx-1 text-center" style="width: 40%">Reps</div>
              <div v-if="exercise.sets[0].isDurationOnly" class="flex-grow-1 mx-1 text-center">Dauer</div>
              <div style="width: 32px"></div>
            </div>

            <!-- Sets Loop -->
            <div v-for="(set, j) in exercise.sets" :key="j" class="d-flex align-center py-2" :style="set.isCompleted ? 'opacity: 0.5;' : ''">
              <div class="text-caption font-weight-bold d-flex align-center justify-center mr-2" style="width: 28px">
                <v-icon v-if="set.isCompleted" small color="success">mdi-check-circle</v-icon>
                <span v-else class="grey--text">#{{ set.index }}</span>
              </div>

              <!-- Inputs -->
              <v-text-field
                v-if="!set.isDurationOnly"
                v-model="set.fillWeight"
                dense
                solo
                flat
                hide-details
                background-color="rgba(0, 0, 0, 0.4)"
                class="mx-1 custom-auto-input"
                type="number"
                suffix="kg"
                :disabled="set.isCompleted"
                style="font-size: 0.9rem"></v-text-field>

              <v-text-field
                v-if="!set.isDurationOnly"
                v-model="set.fillReps"
                dense
                solo
                flat
                hide-details
                background-color="rgba(0, 0, 0, 0.4)"
                class="mx-1 custom-auto-input"
                type="number"
                :disabled="set.isCompleted"
                style="font-size: 0.9rem"></v-text-field>

              <v-text-field
                v-if="set.isDurationOnly || set.fillDuration > 0"
                v-model="set.fillDuration"
                dense
                solo
                flat
                hide-details
                background-color="rgba(0, 0, 0, 0.4)"
                class="mx-1 custom-auto-input"
                type="number"
                :suffix="set.durationUnit || 's'"
                :disabled="set.isCompleted"
                style="font-size: 0.9rem"></v-text-field>

              <div style="width: 32px; display: flex; justify-content: center">
                <v-btn v-if="!set.isCompleted" icon x-small color="red lighten-1" @click="removeSet(i, j)">
                  <v-icon size="18">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Footer -->
        <div class="pa-5 pt-2 relative" style="background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent); z-index: 1">
          <v-btn
            large
            rounded
            block
            class="font-weight-bold mb-3 elevation-10 black--text"
            :style="{ background: splitColor }"
            @click="confirmAutoFinish">
            Jetzt Eintragen & Beenden
            <v-icon right size="18" color="black">mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn text rounded block color="grey lighten-1" @click="showAutoFinishDialog = false" style="font-size: 0.85rem; letter-spacing: 0.5px">
            Abbrechen
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <transition name="grow">
      <div v-if="getSelectedExercise" class="overlay" @mousedown="dragStart" @touchstart="dragStart">
        <div class="main-card-container" :class="{ 'is-dragging-over': dragState.active }">
          <v-card class="dialog-card fullscreen px-4" style="padding-bottom: 50px" dark>
            <v-carousel
              style="margin-top: 10px"
              v-if="!getSelectedExercise.isSuperset"
              height="220px"
              hide-delimiters
              :show-arrows="selectedExerciseImageCarousel.length > 1 && false">
              <v-carousel-item
                @click="showImage(getSelectedExercise)"
                style="cursor: pointer"
                class="text-center"
                v-for="(item, i) in selectedExerciseImageCarousel"
                :key="i">
                <v-img contain :src="item.src" height="200px">
                  <template #placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center"><v-progress-circular indeterminate /></v-row>
                  </template>
                </v-img>
                <!-- 
                <span class="caption-text">
                    {{ selectedExerciseImageCarousel.length > 1 ? "Schritt " + (i + 1) + ": " : "" }} {{ item.description }}</span
                  >
                -->
              </v-carousel-item>
            </v-carousel>
            <div v-else class="superset-image-container d-flex justify-center align-center" style="height: 220px">
              <div
                v-for="subEx in getSelectedExercise.exercises"
                :key="subEx.instanceId"
                class="superset-avatar-wrapper mx-2"
                :class="{ 'active-superset-exercise': getActiveSupersetSubExercise && getActiveSupersetSubExercise.instanceId === subEx.instanceId }"
                @click="showImage(subEx)"
                style="cursor: pointer">
                <div>
                  <v-avatar size="100" class="elevation-4">
                    <v-img :src="getImageForExercise(subEx)" />
                  </v-avatar>
                  <div class="caption text-center mt-2 white--text text-truncate" style="max-width: 100px">
                    {{ subEx.title }}
                  </div>
                </div>
              </div>
            </div>
            <v-card-text ref="dialogContent" class="dialog-content mt-3 px-0">
              <v-alert
                style="background: rgba(255, 255, 255, 0.05); max-width: 100%"
                v-if="selectedExerciseMethodDetail"
                :color="selectedExerciseMethodDetail.color"
                border="left"
                elevation="2"
                colored-border
                class="mb-4 intensity-alert"
                dark>
                <div class="d-flex align-center">
                  <v-icon class="mr-3">{{ selectedExerciseMethodDetail.icon }}</v-icon>
                  <div class="intensity-alert-title">{{ selectedExerciseMethodDetail.descriptionShort }}</div>
                  <v-btn icon small class="ml-auto" @click="toggleIntensityAlertExpansion"
                    ><v-icon>{{ isIntensityAlertExpanded ? "mdi-chevron-up" : "mdi-information-outline" }}</v-icon></v-btn
                  >
                </div>
                <v-expand-transition>
                  <div v-show="isIntensityAlertExpanded" style="max-width: 80vw" class="intensity-details-expanded mt-2">
                    <v-divider class="my-3"></v-divider>
                    <div class="px-1 content-wrapper">
                      <p class="subtitle-1 font-weight-bold">{{ selectedExerciseMethodDetail.details.summary }}</p>
                      <p class="body-2 mb-4">{{ selectedExerciseMethodDetail.description }}</p>
                      <div v-if="selectedExerciseMethodDetail.details.advantages && selectedExerciseMethodDetail.details.advantages.length">
                        <h4
                          class="mt-4 mb-1 text-uppercase text-caption"
                          :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color) }">
                          Vorteile
                        </h4>
                        <v-list dense color="transparent" class="pa-0">
                          <v-list-item
                            v-for="(advantage, index) in selectedExerciseMethodDetail.details.advantages"
                            :key="'adv-' + index"
                            class="px-0 py-1">
                            <v-list-item-icon class="mr-2 my-0"
                              ><v-icon small :color="getIconColorForItem(advantage, 'advantage')">{{ advantage.icon }}</v-icon></v-list-item-icon
                            >
                            <v-list-item-content class="py-0"
                              ><v-list-item-title class="body-2 white--text">{{ advantage.text }}</v-list-item-title></v-list-item-content
                            >
                          </v-list-item>
                        </v-list>
                      </div>
                      <div v-if="selectedExerciseMethodDetail.details.whyDoIt && selectedExerciseMethodDetail.details.whyDoIt.length">
                        <h4
                          class="mt-4 mb-1 text-uppercase text-caption"
                          :style="{ color: getSectionHeadlineColor(selectedExerciseMethodDetail.color, true) }">
                          Warum diese Technik?
                        </h4>
                        <v-list dense color="transparent" class="pa-0">
                          <v-list-item
                            v-for="(reason, index) in selectedExerciseMethodDetail.details.whyDoIt"
                            :key="'reason-' + index"
                            class="px-0 py-1">
                            <v-list-item-icon class="mr-2 my-0"
                              ><v-icon small :color="getIconColorForItem(reason, 'reason')">{{ reason.icon }}</v-icon></v-list-item-icon
                            >
                            <v-list-item-content class="py-0"
                              ><v-list-item-title class="body-2 white--text">{{ reason.text }}</v-list-item-title></v-list-item-content
                            >
                          </v-list-item>
                        </v-list>
                      </div>
                    </div>
                    <v-divider class="mt-4 mb-2"></v-divider>
                    <div class="d-flex justify-end pa-1">
                      <v-btn text small :color="selectedExerciseMethodDetail.color" @click="toggleIntensityAlertExpansion"
                        ><v-icon left small>mdi-chevron-up</v-icon>Weniger anzeigen</v-btn
                      >
                    </div>
                  </div>
                </v-expand-transition>
              </v-alert>
              <v-card
                v-if="activeSplit && !activeSplit.isHyrox"
                flat
                class="mb-4 pa-4 equipment-card"
                :class="{ 'orange--border': hasWarmup && slideIndex === 0 }">
                <template
                  v-if="
                    getSelectedExercise &&
                    getSelectedExercise.general_exercise_settings &&
                    getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                  ">
                  <div class="preset-label">Geräteeinstellungen</div>
                  <div class="plan-settings">
                    <v-chip
                      v-for="(setting, i) in getSelectedExercise.general_exercise_settings.filter((s) => s.value)"
                      :key="'device-' + i"
                      outlined
                      small
                      class="preset-chip mr-2 mb-2">
                      {{ setting.text }}: {{ setting.value }}
                    </v-chip>
                    <span
                      v-if="
                        !getSelectedExercise.general_exercise_settings || !getSelectedExercise.general_exercise_settings.filter((s) => s.value).length
                      "
                      class="grey--text text--lighten-1 text-caption">
                      Keine spezifischen Geräteeinstellungen hinterlegt.
                    </span>
                  </div>
                </template>
                <template v-if="(displayPlanSettings && displayPlanSettings.settings.length > 0) || displayPlanSettings.note">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <div class="preset-label">{{ slideLabel }}</div>
                    <div
                      v-if="
                        !getSelectedExercise.isSuperset && (hasWarmup || (displayPlanSettings.settings && displayPlanSettings.settings.length > 0))
                      ">
                      <v-btn icon @click="prevSlide" :disabled="slideIndex === 0"><v-icon>mdi-chevron-left</v-icon></v-btn>
                      <v-btn icon @click="nextSlide" :disabled="slideIndex >= totalSlides - 1"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    </div>
                  </div>

                  <div class="plan-settings">
                    <template v-if="hasWarmup && slideIndex === 0">
                      <v-chip v-for="(w, i) in warmupSettings" :key="'w' + i" outlined small class="preset-chip orange mr-2 mb-2"
                        >{{ w.label }}: {{ w.value }}{{ w.unit || "" }}</v-chip
                      >
                    </template>
                    <template v-else-if="displayPlanSettings.settings && displayPlanSettings.settings.length > 0">
                      <v-chip v-for="(s, i) in displayPlanSettings.settings" :key="'p' + i" outlined small class="preset-chip mr-2 mb-2"
                        >{{ s.label }}: {{ s.value }}</v-chip
                      >
                    </template>
                  </div>

                  <div v-if="displayPlanSettings.note" class="note-display mt-3">
                    <v-icon small left color="grey lighten-1">mdi-note-text-outline</v-icon>
                    <p class="note-text mb-0">{{ displayPlanSettings.note }}</p>
                  </div>
                </template>
                <template v-else>
                  <span class="grey--text text--lighten-1 text-caption">Keine spezifischen Satzvorgaben.</span>
                </template>
              </v-card>
              <template v-if="isSensoProActive">
                <v-card flat class="mb-4 pa-4 finished-sets-card">
                  <v-card-title class="pa-0 pb-2">Notizen</v-card-title>
                  <div v-if="currentSensoProSet">
                    <div class="d-flex align-center" v-if="!editingNote">
                      <p class="mb-0 body-1">
                        <strong>Heute:</strong> {{ currentSensoProSet.find((s) => s.text === "note").value || "Keine Notiz" }}
                      </p>
                      <v-spacer></v-spacer>
                      <v-btn icon @click="editCurrentNote"><v-icon>mdi-pencil</v-icon></v-btn>
                    </div>
                    <div v-else>
                      <v-textarea v-model="editableNote" outlined rows="2" label="Heutige Notiz bearbeiten"></v-textarea>
                      <v-card-actions class="pa-0">
                        <v-spacer></v-spacer>
                        <v-btn text @click="cancelEditNote">Abbrechen</v-btn>
                        <v-btn color="primary" @click="saveCurrentNote">Speichern</v-btn>
                      </v-card-actions>
                    </div>
                    <v-divider class="my-4"></v-divider>
                  </div>

                  <div v-if="historicalSensoProNotes.length > 0">
                    <div v-for="(item, i) in historicalSensoProNotes" :key="i" class="mb-2">
                      <div class="text-caption grey--text">{{ item.date }}</div>
                      <p class="body-2 mb-0">{{ item.note }}</p>
                    </div>
                  </div>
                  <div v-else class="text-center grey--text py-2 text-caption">Noch keine früheren Notizen vorhanden.</div>
                </v-card>
              </template>
              <template v-else>
                <v-alert
                  v-if="getSelectedExercise && getSelectedExercise.userNote"
                  border="left"
                  colored-border
                  color="primary"
                  elevation="2"
                  dense
                  class="mb-4"
                  icon="mdi-information-outline">
                  <strong>Notiz</strong><br />
                  <span style="white-space: pre-wrap">{{ getSelectedExercise.userNote }}</span>
                </v-alert>
                <v-card flat class="mb-4 pa-4 finished-sets-card">
                  <div class="history-nav d-flex justify-space-between align-center mb-4">
                    <v-btn icon @click="navigateHistory(1)" :disabled="historyViewIndex >= historicalSessions.length">
                      <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                    <div class="text-subtitle-1 font-weight-bold text-center">
                      <p
                        v-if="
                          historicalSessions[historyViewIndex - 1] &&
                          historicalSessions[historyViewIndex - 1].execution &&
                          historicalSessions[historyViewIndex - 1].execution.title.includes('HYROX')
                        ">
                        {{ getHyroxCategoryDetails(historicalSessions[historyViewIndex - 1].execution.title).text }}
                        <v-icon color="yellow">{{ getHyroxCategoryDetails(historicalSessions[historyViewIndex - 1].execution.title).icon }}</v-icon>
                      </p>
                      {{ historyTitle }}
                    </div>
                    <v-btn icon @click="navigateHistory(-1)" :disabled="historyViewIndex === 0">
                      <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>

                  <v-divider class="mb-2"></v-divider>

                  <transition name="table-container-transition" mode="out-in">
                    <div :key="historyViewIndex">
                      <v-simple-table class="completed-table" v-if="setsToDisplay.length > 0">
                        <thead>
                          <tr>
                            <th v-if="setsToDisplay && setsToDisplay.find((set) => set.subExerciseId)" class="text-center" style="width: 50px">
                              Übung
                            </th>
                            <th class="text-center" style="width: 40px">#</th>
                            <th>Details</th>
                            <th v-if="isFeedbackRelevant" class="text-center" style="width: 80px; white-space: nowrap">Feedback</th>
                          </tr>
                        </thead>
                        <transition-group tag="tbody" name="row">
                          <tr
                            v-for="(set, idx) in setsToDisplay"
                            :key="set.id || idx"
                            @click="isTodaySelected ? (set.skipped ? redoWarmup() : promptDeleteSet(idx, set)) : null"
                            :class="{
                              'clickable-row': isTodaySelected && !isWarmupBlocked,
                              'skipped-warmup-row': set.skipped,
                            }"
                            @click.middle="isTodaySelected && set.skipped ? redoWarmup() : null">
                            <td v-if="set.subExerciseId || set[0].subExerciseId" class="text-center">
                              <v-avatar size="28" class="clickable-avatar" @click.stop="showImage(getSubExerciseForSet(set))">
                                <v-img :src="getImageForExercise(getSubExerciseForSet(set))" />
                              </v-avatar>
                            </td>
                            <td class="text-center">
                              <template v-if="hasWarmup && idx === 0">
                                <span v-if="set.skipped" class="grey--text text--lighten-1">--</span>
                                <span v-else>🔥</span>
                              </template>
                              <template v-else>{{ hasWarmup ? realSetNumber(idx) : idx + 1 }}</template>
                            </td>
                            <td :class="{ 'orange--text': hasWarmup && idx === 0 && !set.skipped, 'grey--text text--lighten-1': set.skipped }">
                              {{ set.skipped ? "Warmup übersprungen" : formatSetText(set) }}
                            </td>
                            <td v-if="isFeedbackRelevant" class="text-center" @click.stop>
                              <v-menu
                                v-if="!set.skipped && (getWeightDeviation(set, idx).deviated || hasRating(set) || isTodaySelected)"
                                v-model="set.showMenu"
                                offset-y
                                top
                                :close-on-content-click="false"
                                transition="scale-transition">
                                <template v-slot:activator="{ on, attrs }">
                                  <div v-bind="attrs" v-on="on" class="feedback-activator" @click="openFeedbackMenu(set, idx)">
                                    <span v-if="getFeedbackIcon(set, idx).isEmoji" class="feedback-emoji">
                                      {{ getFeedbackIcon(set, idx).icon }}
                                    </span>
                                    <v-icon v-else :color="getFeedbackIcon(set, idx).color" class="rating-icon">
                                      {{ getFeedbackIcon(set, idx).icon }}
                                    </v-icon>
                                  </div>
                                </template>

                                <v-card v-if="tooltipData" class="feedback-tooltip-card-v2 glass-card-dialog">
                                  <v-card-title class="pb-2">
                                    <span class="feedback-emoji mr-3" style="font-size: 1.5rem">{{ tooltipData.icon }}</span>
                                    <span class="title" :style="{ color: tooltipData.color }">{{ tooltipData.title }}</span>
                                    <v-spacer />
                                  </v-card-title>

                                  <v-divider class="mx-4"></v-divider>

                                  <v-card-text class="pt-4 pb-2 white--text">
                                    <div v-for="(item, itemIdx) in tooltipData.details" :key="item.label" class="d-flex justify-space-between mb-2">
                                      <span class="grey--text">{{ item.label }}</span>
                                      <span class="font-weight-bold" :class="item.color || 'white--text'">
                                        {{ item.value }}
                                      </span>
                                    </div>
                                  </v-card-text>

                                  <v-card-actions v-if="isTodaySelected && !hasRating(set)" class="d-flex justify-center pa-2">
                                    <v-btn text small color="primary" @click="openRetrospectiveFeedback(set, idx)">Jetzt bewerten</v-btn>
                                  </v-card-actions>
                                </v-card>
                              </v-menu>
                            </td>
                          </tr>
                        </transition-group>
                      </v-simple-table>
                      <div v-else class="no-sets text-center pa-4 grey--text">An diesem Tag wurden keine Sätze für diese Übung aufgezeichnet.</div>
                    </div>
                  </transition>
                </v-card>
              </template>

              <div class="premium-input-card mb-6">
                <component
                  v-if="isTodaySelected && exerciseForInput && !currentSensoProSet"
                  :is="currentInputComponent"
                  :is-hyrox="activeSplit.isHyrox"
                  :exercise="exerciseForInput"
                  :completed-sets="currentInputCompletedSets"
                  :completed-sets-array="currentInputCompletedSetsArray"
                  :historical-sessions="historicalSessions"
                  :is-competition="activeSplit.isCompetition"
                  :warmup="hasWarmup && isFirstSubExercise"
                  :plan-settings="getCurrentSatzVorgaben"
                  @add-set="handleAddSet"
                  @update-slide-index="slideIndex = $event"
                  ref="inputComponentSection">
                  <template #add-button
                    ><v-btn color="orange" class="ma-2">{{ addSetLabel }}</v-btn></template
                  >
                </component>
              </div>
            </v-card-text>
            <v-card flat class="mb-4 pa-4 finished-sets-card rounded-xl">
              <div class="subtitle-1 font-weight-bold mb-2">Notiz</div>
              <v-textarea
                v-model="currentExerciseNote"
                placeholder="Füge eine Notiz hinzu, die dir bei kommenden Trainings angezeigt wird..."
                rows="3"
                no-resize
                outlined
                dense
                hide-details></v-textarea>
            </v-card>
          </v-card>
        </div>
        <div v-if="dragState.avatarExercise" class="drag-avatar" :class="{ 'on-target': dragState.dropZoneActive }" :style="dragAvatarStyle">
          <v-img :src="getImageForExercise(dragState.avatarExercise)" cover />
        </div>
      </div>
    </transition>

    <transition name="rest-timer-overlay-fade">
      <div v-if="showFeedbackOverlay" class="feedback-overlay d-flex justify-center align-center">
        <v-card dark class="feedback-card pa-4 text-center elevation-20 rounded-xl d-flex flex-column" width="95%" max-width="450px">
          <div class="feedback-card__header">
            <div style="position: absolute; top: 12px; right: 12px; z-index: 10">
              <v-btn icon small @click="showInfoView" class="mr-1" v-if="feedbackState !== 'info'"><v-icon>mdi-information-outline</v-icon></v-btn>
              <v-btn icon small @click="minimizeFeedback" class="mr-1" v-if="feedbackState !== 'info'"><v-icon>mdi-close</v-icon></v-btn>
            </div>
            <div class="mb-4"><v-img src="/img/roody_logo.png" height="50px" contain /></div>
          </div>

          <div class="feedback-card__content">
            <template v-if="isTimerActive">
              <div
                class="text-h5 font-weight-bold mb-1 status-title"
                :class="{ 'final-state': restTimerRemaining <= 0 }"
                :style="{ color: restTimerProgressColor, letterSpacing: '0.03em !important', textTransform: 'uppercase' }">
                {{ restTimerRemaining > 0 ? "Pause" : overtimeSeconds > 0 ? "Überzeit" : "Fertig!" }}
              </div>
              <div class="my-auto d-flex flex-column align-center">
                <v-progress-circular
                  :rotate="-90"
                  :size="$vuetify.breakpoint.xsOnly ? 170 : 180"
                  :width="$vuetify.breakpoint.xsOnly ? 12 : 14"
                  :value="restTimerProgress"
                  :color="restTimerProgressColor"
                  class="my-4"
                  :style="{ '--progress-color': restTimerProgressColor }">
                  <span
                    class="countdown-text"
                    :class="{ 'countdown-text-smaller': isCountdownTextTooLong, 'pulse-warning': restTimerRemaining > 0 && restTimerRemaining <= 5 }"
                    :style="{ color: restTimerProgressColor }">
                    {{
                      formatCountdownDisplay(
                        restTimerRemaining > 0 ? restTimerRemaining : overtimeSeconds,
                        restTimerRemaining <= 0 && overtimeSeconds > 0,
                      )
                    }}
                  </span>
                </v-progress-circular>
                <div class="text-body-2 grey--text text--lighten-1">Geplant: {{ formatTimeForDisplay(plannedRestDuration) }}</div>
              </div>
            </template>

            <v-fade-transition mode="out-in">
              <div v-if="feedbackState === 'rating'" key="rating" class="feedback-section mt-1">
                <div v-if="weightDeviationStatus === 'higher'">
                  <h3 class="font-weight-bold mb-2 celebration-title">🎉 Super! Gewicht gesteigert!</h3>
                  <p class="body-1 mb-3">Wie hat sich das neue Gewicht angefühlt?</p>
                </div>
                <div v-else-if="weightDeviationStatus === 'lower'">
                  <h3 class="font-weight-bold mb-2" style="color: #ffcc80">Macht nichts, jeder Tag ist anders!</h3>
                  <p class="body-1 mb-3">Bewerte die Schwierigkeit mit dem angepassten Gewicht:</p>
                </div>
                <div v-else-if="feedbackState === 'timer_only'">
                  <v-card-title class="justify-center font-weight-bold pt-0 pb-1">Super Satz!</v-card-title>
                </div>

                <div v-else><v-card-title class="justify-center font-weight-bold pt-0 pb-1">Wie war der Satz?</v-card-title></div>
                <div class="rating-options-container">
                  <div class="rating-option rating-hard" :class="{ 'is-selected': selectedDifficulty === 'hard' }" @click="handleSmileyClick('hard')">
                    <div class="rating-emoji">🥵</div>
                    <div class="rating-text">
                      <div class="rating-label">Zu Schwer</div>
                      <div class="rating-desc">Wiederholungen kaum geschafft</div>
                    </div>
                  </div>
                  <div
                    class="rating-option rating-medium"
                    :class="{ 'is-selected': selectedDifficulty === 'medium' }"
                    @click="handleSmileyClick('medium')">
                    <div class="rating-emoji">🙂</div>
                    <div class="rating-text">
                      <div class="rating-label">Perfekt</div>
                      <div class="rating-desc">Fordernd, aber gute Form</div>
                    </div>
                  </div>
                  <div class="rating-option rating-easy" :class="{ 'is-selected': selectedDifficulty === 'easy' }" @click="handleSmileyClick('easy')">
                    <div class="rating-emoji">😎</div>
                    <div class="rating-text">
                      <div class="rating-label">Zu Leicht</div>
                      <div class="rating-desc">Deutlich mehr wäre möglich</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="feedbackState === 'show_weights'" key="weights" class="feedback-section mt-4">
                <p class="body-1 mb-3">Passe das Gewicht für dein <b>nächstes Training</b> an:</p>

                <v-text-field
                  v-model.number="adjustedWeight"
                  label="Neues Gewicht"
                  outlined
                  dense
                  dark
                  suffix="kg"
                  type="number"
                  inputmode="numeric"
                  class="mt-4 mx-2"
                  @focus="$event.target.select()"></v-text-field>
                <v-btn
                  small
                  text
                  color="grey"
                  @click="
                    feedbackState = 'rating'
                    adjustedWeight = null
                  "
                  class="mt-1">
                  Abbrechen
                </v-btn>
              </div>
              <div v-else-if="feedbackState === 'info'" key="info" class="feedback-section-info">
                <v-card-title class="justify-center font-weight-bold pt-0">Dein Feedback zählt!</v-card-title>
                <v-card-text class="white--text mt-2 text-left">
                  <p class="body-2">
                    Dein Feedback nach jedem Satz hilft uns oder deinem Trainer, deinen Trainingsplan kontinuierlich zu optimieren.
                  </p>
                  <v-divider class="my-3 gradient-divider"></v-divider>
                  <h4 class="font-weight-bold mb-2 text-subtitle-1">Was bedeuten die Bewertungen?</h4>
                  <v-list dense color="transparent" class="info-list">
                    <v-list-item class="mb-2 px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">🥵</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Zu Schwer</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Du hast die Wiederholungen kaum oder gar nicht geschafft. Das Gewicht war definitiv zu hoch.</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item class="mb-2 px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">🙂</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Perfekt</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Der Satz war fordernd, aber du hast alle Wiederholungen mit guter Form geschafft. Genau richtig!</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                    <v-list-item class="px-0">
                      <v-list-item-icon class="mr-4 rating-info-emoji">😎</v-list-item-icon>
                      <v-list-item-content
                        ><v-list-item-title>Zu Leicht</v-list-item-title
                        ><v-list-item-subtitle class="info-subtitle"
                          >Der Satz war einfach und du hättest ohne Probleme deutlich mehr Wiederholungen oder Gewicht bewältigen
                          können.</v-list-item-subtitle
                        ></v-list-item-content
                      >
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </div>
            </v-fade-transition>
          </div>

          <div class="feedback-card__footer mt-4">
            <div v-if="feedbackState === 'info'" class="mt-auto">
              <v-btn block large rounded color="primary" @click="hideInfoView" class="elevation-4"><v-icon left>mdi-arrow-left</v-icon>Zurück</v-btn>
            </div>
            <div v-else class="mt-auto">
              <v-checkbox
                v-if="!isTimerActive && !disableFeedbackForExercise"
                v-model="doNotShowAgain"
                label="Für diese Übung nicht mehr fragen"
                dense
                class="mt-4"></v-checkbox>
              <div class="mt-1">
                <v-btn
                  block
                  x-large
                  rounded
                  :color="buttonColor"
                  @click="minimizeFeedback()"
                  class="elevation-4"
                  :disabled="!isFeedbackCompleted"
                  style="min-height: 56px; letter-spacing: 0.05em">
                  <v-icon left size="26">{{ buttonIcon }}</v-icon>
                  {{ buttonText }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </transition>

    <v-dialog v-model="exerciseDetailDialog" max-width="600px" dark>
      <v-card v-if="detailDialogExercise" class="glass-card-dialog">
        <v-toolbar flat color="transparent">
          <v-toolbar-title class="font-weight-bold">{{ detailDialogExercise.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="exerciseDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-carousel height="350px" hide-delimiters :show-arrows="detailCarouselItems.length > 1">
          <v-carousel-item class="text-center" v-for="(item, i) in detailCarouselItems" :key="i">
            <div>
              <v-img contain :src="item.src" height="280px">
                <template #placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate />
                  </v-row>
                </template>
              </v-img>
              <div class="caption-text grey--text text--lighten-1 pa-3">
                {{ item.description }}
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>
    <v-fade-transition>
      <div v-if="showHyroxTransition" class="hyrox-transition-overlay">
        <v-card class="hyrox-transition-card pa-5 text-center">
          <div class="transition-title mb-1">NÄCHSTE ÜBUNG</div>
          <v-avatar size="100" class="my-4 elevation-6">
            <v-img v-if="transitionData" :src="transitionData.imageSrc" />
          </v-avatar>
          <div v-if="transitionData" class="text-h6 font-weight-bold">{{ transitionData.title }}</div>
          <v-progress-linear indeterminate color="yellow" rounded class="mt-5"></v-progress-linear>
        </v-card>
      </div>
    </v-fade-transition>
    <v-fade-transition>
      <div v-if="showAutoTransition" class="auto-transition-overlay">
        <v-card class="auto-transition-card pa-5 text-center" :style="{ borderColor: splitColor, boxShadow: `0 8px 40px ${splitColor}33` }">
          <div class="transition-title mb-1" :style="{ color: splitColor }">Nächste Übung</div>
          <v-avatar size="100" class="my-4 elevation-6" :style="{ border: `2px solid ${splitColor}` }">
            <v-img v-if="transitionData" :src="transitionData.imageSrc" />
          </v-avatar>
          <div v-if="transitionData" class="text-h6 font-weight-bold white--text mb-2">{{ transitionData.title }}</div>
          <div class="transition-countdown my-3 white--text">
            Wechsel in <span class="font-weight-black text-h5" :style="{ color: splitColor }">{{ transitionCountdown.toFixed(1) }}s</span>
          </div>
          <v-progress-linear :value="(transitionCountdown / 1.5) * 100" color="success" rounded height="6" class="mt-3 mb-5"></v-progress-linear>
          <v-btn rounded outlined color="red lighten-1" class="font-weight-bold px-6" @click="cancelAutoTransition"> Abbrechen </v-btn>
        </v-card>
      </div>
    </v-fade-transition>
  </v-card>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex"
import WeightRepInput from "./setInputTypes/weightRepInput.vue"
import SensoProInput from "./setInputTypes/sensoProInput.vue"
import TimeInput from "./setInputTypes/timeInput.vue"
import DistanceRepInput from "./setInputTypes/distanceRepInput.vue" // NEU
import HyroxInput from "./setInputTypes/hyroxInput.vue" // NEU

import { intensityTechniqueMapping } from "../utils/intensityTechniques.js"
import PersonalRecordAnimation from "./PersonalRecordAnimation.vue" // Pfad prüfen!

import moment from "moment"
import confetti from "canvas-confetti"

export default {
  name: "ExerciseList",
  components: { WeightRepInput, TimeInput, SensoProInput, DistanceRepInput, PersonalRecordAnimation, HyroxInput }, // HIER HINZUFÜGEN
  isSelectionDialogActive: false,
  selectionDialogMode: "swap", // 'swap' oder 'add'
  selectionDialogExerciseToSwap: null,
  selectionDialogInsertionIndex: 0,
  props: ["initialHeight"],
  data() {
    return {
      skippedExercises: [], // NEU: Liste der ausgeblendeten IDs
      showHyroxTransition: false,
      showAutoTransition: false,
      transitionCountdown: 1.5,
      autoTransitionInterval: null,
      transitionData: null,
      finalSet: null,
      tooltipData: null,
      showPRAnimation: false,
      prAnimationData: {
        recordType: "",
        newValue: "",
      },
      editingNote: false,
      editableNote: "",
      slideIndex: 0,
      intensityTechniqueMapping: intensityTechniqueMapping,
      isIntensityAlertExpanded: false,
      isFirstSetBeingAdded: false,
      previousScrollTop: 0,
      previousTbodyHeight: 0,
      showFeedbackOverlay: false,
      isTimerActive: false,
      pendingSet: null,
      feedbackState: "rating",
      selectedDifficulty: null,
      weightSuggestions: [],
      adjustedWeight: null,
      doNotShowAgain: false,
      disableFeedbackForExercise: false,
      plannedRestDuration: 0,
      restTimerRemaining: 0,
      overtimeSeconds: 0,
      restTimerInterval: null,
      pauseStartTime: null,
      isRetrospective: false,
      historyViewIndex: 0,
      retrospectiveSetIndex: null,
      weightDeviationStatus: "on_track",
      showIncompleteDialog: false, // NEU
      isAddingWarmupSet: false,
      imageDialog: false,
      imageDialogSrc: "",
      exerciseDetailDialog: false, // Steuert die Sichtbarkeit des neuen Dialogs
      detailDialogExercise: null,
      dragState: {
        active: false, // Ist ein Drag-Vorgang aktiv?
        origin: null, // Von welcher Seite kommt der Drag ('left' oder 'right')
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
        avatarExercise: null, // Welche Übung wird gerade gezogen?
        dropZoneActive: false, // Ist der Avatar über der Drop-Zone?
        containerWidth: 0, // Breite des Viewports
        dragThreshold: 100, // Wieviele Pixel für "dropZoneActive"
        isCompleting: false, // Für die Erfolgs-Animation
      },
      showWeightPropagationDialog: false,
      propagationData: null,
      propagationCallback: null,
      showAutoFinishDialog: false, // NEU: Für den Auto-Complete Dialog
      autoCompletePreviewData: [], // NEU: Daten für den Auto-Complete Dialog
      isEditingMinimisedSet: false, // NEU: Flag für Maximierung
    }
  },
  computed: {
    ...mapState("runWorkoutStore", ["selectedExerciseIndex", "activeSplit", "disabledFeedbackExercises", "completedSetsMap", "hubGroupedSessions"]),
    ...mapGetters("runWorkoutStore", [
      "getSelectedExercise",
      "getActiveSupersetSubExercise",
      "getCurrentSatzVorgaben",
      "hubGroupedExercises",
      "getExerciseNote",
      "isGlobalTimerActive", // UMBENANNT
      "timerState", // WICHTIG: Damit der Watcher funktioniert!
      "feedbackEnabled",
      "timerEnabled",
      "autoTransitionEnabled",
    ]),

    splitColor() {
      return this.activeSplit?.fixedVisualization?.color || "#00E5FF"
    },

    visibleWarmupExercises() {
      if (!this.activeSplit) return []
      return this.activeSplit.exercises
        .map((ex, i) => {
          const completedSets = this.completedSetsMap[i] || []
          return { ...ex, originalIndex: i, completedSetCount: completedSets.filter((s) => s.isWarmup && !s.skipped).length }
        })
        .filter((ex) => !ex.isSuperset && ex.sets.length === 0 && ex.warmup && !this.skippedExercises.includes(ex._id))
    },
    visibleRegularExercises() {
      if (!this.activeSplit) return []
      return this.activeSplit.exercises
        .map((ex, i) => {
          const allCompletedSets = this.completedSetsMap[i] || []
          const completedRegularCount = allCompletedSets.filter((s) => !s.isWarmup && !s.skipped).length
          const plannedSetCount = ex.isSuperset ? ex.exercises.reduce((sum, sub) => sum + (sub.sets ? sub.sets.length : 0), 0) : ex.sets?.length || 0
          const isWarmupCompleted = allCompletedSets.some((s) => s.isWarmup && !s.skipped)

          return {
            ...ex,
            originalIndex: i,
            completedSetCount: completedRegularCount,
            plannedSetCount: plannedSetCount,
            isWarmupCompleted: isWarmupCompleted,
          }
        })
        .filter((ex) => {
          if (this.skippedExercises.includes(ex._id)) return false
          if (ex.isSuperset) return true
          return !(ex.sets.length === 0 && ex.warmup)
        })
    },
    warmupOnly() {
      return this.visibleWarmupExercises
    },
    regularExercises() {
      return this.visibleRegularExercises
    },
    currentExerciseNote: {
      get() {
        if (this.selectedExerciseIndex === null) return ""
        // Hole NUR die Notiz, die in DIESER Session im Store gespeichert wurde
        const sessionNote = this.getExerciseNote(this.selectedExerciseIndex)
        if (sessionNote !== undefined) {
          return sessionNote
        }
        // Ansonsten zeige die Notiz vom letzten Mal (aus dem Plan) an
        return this.getSelectedExercise?.userNote || ""
      },
      set(value) {
        if (this.selectedExerciseIndex === null) return
        this.updateExerciseNote({
          exerciseIndex: this.selectedExerciseIndex,
          note: value,
        })
      },
    },
    isFeedbackRelevant() {
      if (!this.getSelectedExercise || (this.activeSplit && this.activeSplit.isHyrox)) return false
      return this.getSelectedExercise.set_exercise_settings?.some((s) => s.text === "Gewicht")
    },
    isWarmupBlocked() {
      return this.hasWarmup && this.completedSetsArray.length > 1
    },
    allExercises() {
      return [...this.warmupOnly, ...this.regularExercises]
    },
    nextExercise() {
      const currentIndex = this.allExercises.findIndex((ex) => ex.originalIndex === this.selectedExerciseIndex)
      if (currentIndex === -1 || currentIndex >= this.allExercises.length - 1) {
        return null
      }
      return this.allExercises[currentIndex + 1]
    },
    previousExercise() {
      const currentIndex = this.allExercises.findIndex((ex) => ex.originalIndex === this.selectedExerciseIndex)
      if (currentIndex <= 0) {
        return null
      }
      return this.allExercises[currentIndex - 1]
    },

    mainCardContainerStyle() {
      // Diese Property wird jetzt für den Container verwendet
      return {
        transform: this.dragState.active ? "scale(0.95)" : "scale(1)",
        filter: this.dragState.active ? "blur(4px)" : "none",
      }
    },
    dragAvatarStyle() {
      // isCompleting ist die Erfolgsanimation (Avatar explodiert)
      if (this.dragState.isCompleting) {
        return {
          opacity: 0,
          transform: "translate(-50%, -50%) scale(20)", // KORREKTUR
          "transition-duration": "0.4s",
          "transition-timing-function": "cubic-bezier(0.5, 0, 0.1, 1)",
        }
      }

      // Wenn kein Avatar da sein soll (weder aktiv, noch beim Zurück-animieren)
      if (!this.dragState.avatarExercise) {
        return { opacity: 0, transform: "translate(-50%, -50%) scale(0.3)", "transition-duration": "0.3s" }
      }

      // Standard-Transition für das Zurück-Animieren (wenn active=false)
      let transitionDuration = "0.3s"

      // Solange der Drag aktiv ist (Finger ist auf dem Schirm)
      if (this.dragState.active) {
        const dragDistance = this.dragState.currentX - this.dragState.startX
        // progress geht von 0 bis 1 (und leicht darüber hinaus, um "overshoot" zu erlauben)
        const dragProgress = Math.min(1.2, Math.abs(dragDistance) / this.dragState.dragThreshold)

        // 1. Position: von der Seite zur Mitte
        // startTranslate ist, wo der Avatar herkommt (z.B. 50vw = ganz rechts)
        const startTranslateX = (this.dragState.origin === "right" ? 1 : -1) * (this.dragState.containerWidth / 2)
        // currentTranslate geht von startTranslateX (bei progress 0) -> 0 (bei progress 1)
        const currentTranslateX = (1 - Math.min(1, dragProgress)) * startTranslateX

        // 2. Skalierung: wird grösser
        const currentScale = 0.5 + dragProgress * 1.0

        // 3. Opacity: wird sichtbar
        const currentOpacity = 0.5 + dragProgress * 0.5

        // Keine CSS-Transition während dem Ziehen
        transitionDuration = "0s"
        return {
          opacity: currentOpacity,
          transform: `translate(-50%, -50%) translateX(${currentTranslateX}px) scale(${currentScale})`,
          "transition-duration": transitionDuration,
        }
      }

      // Fallback: Drag ist nicht aktiv (wurde losgelassen/abgebrochen)
      // -> animiert zu "off-screen" Position zurück
      const startTranslateX = (this.dragState.origin === "right" ? 1 : -1) * (this.dragState.containerWidth / 2)
      return {
        opacity: 0,
        transform: `translate(-50%, -50%) translateX(${startTranslateX}px) scale(0.5)`,
        "transition-duration": transitionDuration,
      }
    },
    isSensoProActive() {
      return this.getSelectedExercise?.title === "Senso Pro"
    },
    currentSensoProSet() {
      if (!this.isSensoProActive || !this.isTodaySelected) return null
      return this.completedSetsArray.length > 0 ? this.completedSetsArray[0] : null
    },
    historicalSensoProNotes() {
      if (!this.isSensoProActive || !this.historicalSessions) return []
      const notes = []
      this.historicalSessions.forEach((session) => {
        if (session.steps && session.steps[0] && Array.isArray(session.steps[0])) {
          const noteEntry = session.steps[0].find((s) => s.text === "note")
          if (noteEntry && noteEntry.value) {
            notes.push({ date: moment(session.date).format("DD.MM.YYYY"), note: noteEntry.value })
          }
        }
      })
      return notes
    },
    exerciseForInput() {
      if (this.getSelectedExercise?.isSuperset) {
        return this.getActiveSupersetSubExercise
      }
      return this.getSelectedExercise
    },
    // In runWorkoutExerciseList.vue -> computed

    detailCarouselItems() {
      const ex = this.detailDialogExercise
      if (!ex) return []

      const imgs = []

      // 1. Füge zuerst das Hauptbild hinzu, falls es existiert.
      if (ex.picture) {
        imgs.push({
          src: this.getImageUrl(ex.picture),
          description: "Hauptansicht",
        })
      }

      // 2. Füge dann alle Bilder aus der Anleitung hinzu, aber nur, wenn sie sich vom Hauptbild unterscheiden.
      if (Array.isArray(ex.manual) && ex.manual.length > 0) {
        ex.manual.forEach((m, index) => {
          if (m.picture) {
            const manualImgSrc = this.getImageUrl(m.picture)
            // Verhindere Duplikate, falls das Hauptbild auch das erste Manual-Bild ist.
            if (!imgs.some((img) => img.src === manualImgSrc)) {
              imgs.push({
                src: manualImgSrc,
                description: m.description || `Schritt ${index + 1}`,
              })
            }
          }
        })
      }

      // 3. Fallback: Wenn nach allem immer noch kein Bild da ist, zeige den Placeholder.
      if (imgs.length === 0) {
        imgs.push({ src: "/img/einmalig/imageUploadPlaceholder.jpg", description: "Kein Bild verfügbar" })
      }

      return imgs
    },
    isTodaySelected() {
      return this.historyViewIndex === 0
    },
    incompleteExercises() {
      if (!this.activeSplit || !this.activeSplit.exercises) return []

      const incomplete = []
      this.activeSplit.exercises.forEach((ex, index) => {
        if (this.skippedExercises.includes(ex._id)) return

        // Logic for supersets
        if (ex.isSuperset) {
          ex.exercises.forEach((subEx) => {
            const completedSets = this.getCompletedSetsForSubExercise(ex, subEx)
            const plannedCount = subEx.sets ? subEx.sets.length : 0
            if (completedSets.length < plannedCount) {
              incomplete.push({
                title: `${subEx.title} (Supersatz)`,
                completed: completedSets.length,
                planned: plannedCount,
              })
            }
          })
          return
        }

        // Logic for single exercises
        // Access the map directly for reactivity
        const completedSets = this.completedSetsMap[index] || []
        const realCompletedCount = completedSets.filter((set) => !set.skipped).length

        let plannedCount = ex.sets.length
        if (ex.warmup) {
          plannedCount += 1
        }

        if (realCompletedCount < plannedCount) {
          incomplete.push({
            title: ex.title,
            completed: realCompletedCount,
            planned: plannedCount,
          })
        }
      })
      return incomplete
    },
    currentExerciseHistory() {
      if (!this.getSelectedExercise) return null

      // 1. Welche ID suchen wir? (Bei Supersatz: Die ID der angezeigten Unter-Übung)
      let targetId = this.getSelectedExercise._id
      if (this.getSelectedExercise.isSuperset) {
        try {
          let filteredSessions = this.hubGroupedSessions
            .filter((session) =>
              session.sets.find((set) =>
                set.steps.find(
                  (step) =>
                    step.subExerciseId === this.getSelectedExercise.exercises[0]._id ||
                    step.subExerciseId === this.getSelectedExercise.exercises[1]._id,
                ),
              ),
            )
            .map(
              (session) =>
                session.sets.filter((sets) =>
                  sets.steps.find(
                    (step) =>
                      step.subExerciseId === this.getSelectedExercise.exercises[0]._id ||
                      step.subExerciseId === this.getSelectedExercise.exercises[1]._id,
                  ),
                )[0],
            )

          //  filteredSessions = filteredSessions.map(session => )
          return { sets: filteredSessions }
        } catch (error) {
          console.error("Fehler beim Filtern der Sessions für Supersatz:", error)
          return null
        }
      }
      // 2. Sammle ALLE Sätze aus der History, die zu dieser ID passen
      // Egal ob sie als Einzelübung oder in einem Supersatz gespeichert wurden
      let allSets = []

      if (this.hubGroupedExercises) {
        this.hubGroupedExercises.forEach((group) => {
          // Fall A: Die Gruppe IST die gesuchte Übung (Einzel-Ausführung)
          const groupId = group.exercise._id || group.exercise
          if (groupId === targetId && group.sets) {
            allSets.push(...group.sets)
          }
          // Fall B: Die Gruppe ist irgendwas anderes (z.B. ein Supersatz), enthält aber Sätze unserer Übung
          else if (group.sets && group.sets.length > 0) {
            const matchingSets = group.sets.filter((s) => s.subExerciseId === targetId)
            allSets.push(...matchingSets)
          }
        })
      }

      if (allSets.length === 0) return null

      // Gib ein Objekt zurück, das so aussieht, wie die UI es erwartet
      // Wir sortieren sicherheitshalber nach Datum neu
      return {
        sets: allSets.sort((a, b) => new Date(b.date) - new Date(a.date)),
      }
    },
    historicalSessions() {
      if (this.activeSplit?.isHyrox) {
        if (!this.hubGroupedSessions || this.selectedExerciseIndex === null) return []

        const history = []
        const currentSegmentIndex = this.selectedExerciseIndex
        // 1. Finde alle vergangenen Rennen mit dem exakt gleichen Titel
        const pastRaces = this.hubGroupedSessions.filter(
          (session) => session.execution && session.execution.title === this.activeSplit.title && session.execution.hyroxSegments,
        )
        pastRaces.forEach((race) => {
          const pastSegment = race.execution.hyroxSegments.find((s) => s.segmentIndex === currentSegmentIndex)
          if (pastSegment) {
            // Erstelle einen "Session"-Eintrag, der mit setsToDisplay kompatibel ist
            history.push({
              date: race.execution.createdAt,
              execution: race.execution,
              steps: [{ ...pastSegment }], // Wandle Segment in einen "step" um
            })
          }
        })
        return history
      }
      if (!this.currentExerciseHistory || !this.currentExerciseHistory.sets) return []
      return this.currentExerciseHistory.sets.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    setsToDisplay() {
      if (this.historyViewIndex === 0) {
        return this.completedSetsArray
      }
      const historicalSession = this.historicalSessions[this.historyViewIndex - 1]
      if (historicalSession && historicalSession.steps) {
        return historicalSession.steps.map((step) => {
          const setArray = []
          if (step.duration != null) {
            setArray.push({ text: "Dauer", value: Number(step.duration).toFixed(2), subExerciseId: step.subExerciseId ? step.subExerciseId : null })
          }
          if (step.weight != null) {
            setArray.push({ text: "Gewicht", value: step.weight, subExerciseId: step.subExerciseId ? step.subExerciseId : null })
          }
          if (step.reps != null && step.reps > 0) {
            // Nur anzeigen, wenn Reps vorhanden sind (z.B. Wall Balls)
            setArray.push({ text: "Wiederholungen", value: step.reps, subExerciseId: step.subExerciseId ? step.subExerciseId : null })
          }
          if (step.distance != null && step.distance > 0) {
            // Nur anzeigen, wenn Distanz vorhanden ist
            setArray.push({ text: "Distanz", value: step.distance, subExerciseId: step.subExerciseId ? step.subExerciseId : null })
          }

          // Fallback für das ganz alte Zeitformat
          if (step.time != null) {
            setArray.push({ text: "Dauer", value: Number(step.time).toFixed(2), subExerciseId: step.subExerciseId ? step.subExerciseId : null })
          }
          // --- ENDE DER NEUEN LOGIK ---
          return setArray
        })
      }
      return []
    },
    historyTitle() {
      if (this.historyViewIndex === 0) {
        return "Heutiges Training"
      }
      const session = this.historicalSessions[this.historyViewIndex - 1]
      if (session) {
        if (session.date) {
          //return this.$moment.unix(session.sets[0].timestamp)
          // Gibt für die Anzeige den vollen Titel zurück, z.B. "HYROX (Men Pro)"
          return `Training vom ${this.$moment(session.date).format("DD.MM.YYYY")}`
        }
        if (session.timestamp) {
          return `Training vom ${this.$moment.unix(session.timestamp).format("DD.MM.YYYY")}`
        }
        return "Vergangenes Training"
      }
      return "Verlauf"
    },
    warmupOnly() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const completedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die auch Aufwärmsätze sind
          const completedWarmupCount = completedSets.filter((s) => s.isWarmup && !s.skipped).length
          return { ...ex, originalIndex: i, completedSetCount: completedWarmupCount }
        })
        .filter((ex) => !ex.isSuperset && ex.sets.length === 0 && ex.warmup)
    },

    regularExercises() {
      if (!this.activeSplit) return []
      const map = this.completedSetsMap
      return this.activeSplit.exercises
        .map((ex, i) => {
          const allCompletedSets = map[i] || []
          // Zähle nur abgeschlossene Sätze, die KEINE Aufwärmsätze sind
          const completedRegularCount = allCompletedSets.filter((s) => !s.isWarmup && !s.skipped).length
          const plannedSetCount = ex.isSuperset ? ex.exercises[0]?.sets?.length || 0 : ex.sets?.length || 0

          return {
            ...ex,
            originalIndex: i,
            completedSetCount: completedRegularCount, // Hier wird die korrigierte Anzahl verwendet
            plannedSetCount: plannedSetCount,
          }
        })
        .filter((ex) => {
          if (ex.isSuperset) return true
          return !(ex.sets.length === 0 && ex.warmup)
        })
    },
    selectedExerciseImageCarousel() {
      const ex = this.getSelectedExercise
      if (!ex) return []
      const imgs = []
      imgs.push({ src: this.getImageForExercise(ex) })
      return imgs
    },
    hasWarmup() {
      return !!this.getSelectedExercise?.warmup
    },
    warmupSettings() {
      if (!this.getSelectedExercise || !this.getSelectedExercise.warmup) return []
      let ws = this.getSelectedExercise.warmup.filter((s) => typeof s.value !== "undefined") || []
      const s = []
      const w = ws.find((s) => s.text === "Gewicht")
      if (w) s.push({ label: "Gewicht", value: w.value + "kg" })
      const minR = ws.find((s) => s.text === "Min Wiederholungen")
      const maxR = ws.find((s) => s.text === "Max Wiederholungen")
      if (minR && maxR) {
        s.push({ label: "WH", value: `${minR.value} - ${maxR.value}` })
      } else if (minR) {
        s.push({ label: "WH", value: minR.value })
      } else if (maxR) {
        s.push({ label: "WH", value: maxR.value })
      }
      const d = ws.find((s) => s.text === "Dauer")
      if (d) s.push({ label: "Dauer", value: d.value + " Minuten" })
      return s
    },
    currentPlanSettingsRaw() {
      // Die gesamte Logik steckt bereits in unserem Vuex-Getter.
      // Wir müssen ihn hier nur zurückgeben.
      return this.getCurrentSatzVorgaben || []
    },
    viewedSlidePresets() {
      const ex = this.getSelectedExercise
      if (!ex) return []

      // Fall 1: Supersätze (zeigt jetzt auch die korrekten Vorgaben)
      if (ex.isSuperset) {
        const subEx = this.getActiveSupersetSubExercise
        if (!subEx || !subEx.sets) return []
        // Bei Supersätzen wird der slideIndex ignoriert, es wird immer der nächste Satz der aktiven Übung angezeigt.
        const completedSets = this.completedSetsMap[this.selectedExerciseIndex] || []
        const completedForThisSubEx = completedSets.filter((set) => set.subExerciseId === subEx._id && !set.skipped).length
        return subEx.sets[completedForThisSubEx] || subEx.sets[subEx.sets.length - 1] || []
      }

      // Fall 2: Aufwärmsatz wird angezeigt
      if (this.hasWarmup && this.slideIndex === 0) {
        return ex.warmup || []
      }

      // Fall 3: Ein regulärer Satz wird angezeigt
      const regularSetIndex = this.hasWarmup ? this.slideIndex - 1 : this.slideIndex
      if (ex.sets && regularSetIndex >= 0 && regularSetIndex < ex.sets.length) {
        return ex.sets[regularSetIndex]
      }

      // Fallback
      return []
    },

    // Diese Property funktioniert mit der korrigierten `viewedSlidePresets` jetzt richtig.
    displayPlanSettings() {
      const currentSetSettings = this.viewedSlidePresets
      if (!Array.isArray(currentSetSettings) || currentSetSettings.length === 0) {
        return { settings: [], note: null } // Gibt jetzt ein Objekt zurück
      }

      const settings = []
      // Die Notiz wird separat behandelt
      const noteSetting = currentSetSettings.find((s) => s.text === "Notiz")

      // Alle anderen Vorgaben werden wie gewohnt als Chips verarbeitet
      const distance = currentSetSettings.find((s) => s.text === "Distanz")
      if (distance?.value) settings.push({ label: "Distanz", value: `${distance.value}m` })

      const weight = currentSetSettings.find((s) => s.text === "Gewicht")
      if (weight?.value) settings.push({ label: "Gewicht", value: `${weight.value}kg` })

      const minRep = currentSetSettings.find((s) => s.text === "Min Wiederholungen")?.value
      const maxRep = currentSetSettings.find((s) => s.text === "Max Wiederholungen")?.value
      if (minRep && maxRep) settings.push({ label: "WH", value: `${minRep} - ${maxRep}` })
      else if (minRep || maxRep) settings.push({ label: "WH", value: minRep || maxRep })

      const duration = currentSetSettings.find((s) => s.text === "Dauer")
      if (duration?.value) {
        const unit = duration.unit || "min"
        settings.push({ label: "Dauer", value: `${duration.value}${unit}` })
      }

      const pauseSetting = currentSetSettings.find((s) => s.text === "Pause")
      if (pauseSetting?.value > 0) settings.push({ label: "Pause", value: `${pauseSetting.value}s` })

      return {
        settings: settings,
        note: noteSetting?.value || null, // Die Notiz wird als separater Wert zurückgegeben
      }
    },
    // ...
    // KORREKTUR 3: Macht die Slide-Navigation Supersatz-fähig (obwohl sie ausgeblendet ist)
    totalSlides() {
      if (!this.getSelectedExercise) return 0
      if (this.getSelectedExercise.isSuperset) {
        // Bei einem Supersatz entspricht die Anzahl der "Runden" der Satzanzahl der ersten Sub-Übung
        return this.getSelectedExercise.exercises[0]?.sets?.length || 0
      }
      const c = (this.getSelectedExercise.sets || []).length
      return this.hasWarmup ? c + 1 : c
    },

    // KORREKTUR 4: Zeigt den korrekten Label für den nächsten Satz an
    slideLabel() {
      if (!this.getSelectedExercise) return ""

      if (this.getSelectedExercise.isSuperset) {
        const subEx = this.getActiveSupersetSubExercise
        if (!subEx) return "Supersatz"

        // Zähle, wie viele Sätze für DIESE Sub-Übung schon gemacht wurden
        const completedSets = this.completedSetsMap[this.selectedExerciseIndex] || []
        const completedForThisSubEx = completedSets.filter((set) => set.subExerciseId === subEx._id && !set.skipped).length
        return `Vorgabe Satz ${completedForThisSubEx + 1}`
      }

      // Bestehende Logik für Einzelübungen
      if (this.hasWarmup && this.slideIndex === 0) return "🔥 Vorgaben"
      const i = this.hasWarmup ? this.slideIndex : this.slideIndex + 1
      return `Vorgaben Satz ${i}`
    },
    addSetLabel() {
      if (!this.getSelectedExercise) return "Satz hinzufügen"
      if (this.hasWarmup && this.completedSetsArray.length === 0) return "Aufwärmsatz hinzufügen"
      const nI = this.completedSetsArray.length + (this.hasWarmup ? 0 : 1)
      return `Satz ${nI} hinzufügen`
    },
    completedSets() {
      return this.completedSetsArray.length
    },
    completedSetsArray() {
      if (this.selectedExerciseIndex === null) return []
      return this.completedSetsMap[this.selectedExerciseIndex] || []
    },
    currentInputCompletedSetsArray() {
      if (this.getSelectedExercise?.isSuperset) {
        const subEx = this.getActiveSupersetSubExercise
        if (!subEx) return []
        return this.getCompletedSetsForSubExercise(this.getSelectedExercise, subEx)
      }
      return this.completedSetsArray
    },
    currentInputCompletedSets() {
      return this.currentInputCompletedSetsArray.length
    },
    isFirstSubExercise() {
      if (!this.getSelectedExercise?.isSuperset) return true
      const subEx = this.getActiveSupersetSubExercise
      return subEx && this.getSelectedExercise.exercises[0]._id === subEx._id
    },
    currentInputComponent() {
      if (!this.exerciseForInput) return null

      if (this.activeSplit.isHyrox) {
        return "HyroxInput"
      }
      if (this.exerciseForInput.title === "Senso Pro") {
        return "SensoProInput"
      }

      if (this.exerciseForInput.set_exercise_settings?.some((s) => s.text === "Distanz")) {
        return "DistanceRepInput"
      }

      if (this.exerciseForInput.set_exercise_settings?.some((s) => s.text === "Dauer")) {
        return "TimeInput"
      }

      return "WeightRepInput"
    },
    selectedExerciseMethodDetail() {
      if (this.getSelectedExercise && this.getSelectedExercise.method && this.intensityTechniqueMapping[this.getSelectedExercise.method]) {
        return this.intensityTechniqueMapping[this.getSelectedExercise.method]
      }
      return null
    },
    restTimerProgress() {
      // Wenn die Zeit abgelaufen ist oder keine Pause geplant war, ist der Kreis leer.
      if (this.plannedRestDuration <= 0 || this.restTimerRemaining <= 0) {
        return 0
      }
      // Berechne den prozentualen Anteil der *verbleibenden* Zeit.
      return (this.restTimerRemaining / this.plannedRestDuration) * 100
    },
    restTimerProgressColor() {
      // Wenn in Überzeit, eine andere Farbe anzeigen (z.B. rot)
      if (this.overtimeSeconds > 0) {
        return "#E53935" // Ein kräftiges Rot
      }
      // Wenn die Zeit abgelaufen ist (aber keine Überzeit)
      if (this.restTimerRemaining <= 0) {
        return "#4CAF50" // Dein Erfolgs-Grün
      }
      // In den letzten 10 Sekunden
      if (this.restTimerRemaining <= 10) {
        return "#FB8C00" // Dein Warn-Orange
      }
      // Standardfarbe
      return this.selectedExerciseMethodDetail?.color || "primary"
    },
    isCountdownTextTooLong() {
      const seconds = this.restTimerRemaining > 0 ? this.restTimerRemaining : this.overtimeSeconds
      return String(seconds).length >= 3
    },
    isFeedbackCompleted() {
      if (this.doNotShowAgain) return true

      if (this.feedbackState === "timer_only") {
        return true
      }
      if (this.feedbackState === "rating") return !!this.selectedDifficulty
      if (this.feedbackState === "show_weights") return this.adjustedWeight !== null
      return true
    },
    buttonColor() {
      if (!this.isFeedbackCompleted) return "blue-grey darken-1"
      if (this.doNotShowAgain) return "primary"
      const colors = { easy: "green", medium: "orange", hard: "red" }
      return colors[this.selectedDifficulty] || "primary"
    },
    buttonText() {
      return this.isGlobalTimerActive ? "Weiter" : "Bestätigen"
    },
    buttonIcon() {
      return this.isGlobalTimerActive ? "mdi-skip-next" : "mdi-check"
    },
    isTimerMinimized() {
      return this.timerState ? this.timerState.isMinimized : false
    },
  },
  methods: {
    ...mapActions("runWorkoutStore", [
      "setSelectedExerciseIndex",
      "addCompletedSet",
      "removeCompletedSet",
      "clearCompletedSets",
      "addDisabledFeedbackExercise",
      "replaceCompletedSet",
      "updateCompletedSet", // NEU
      "swapExerciseTemporarily", // NEU
      "addExerciseTemporarily", // NEU
      "updateFutureExerciseWeights",
      "checkForPersonalRecord",
      "checkForPersonalRecord",
      "updateExerciseNote",
      "startTimer",
      "stopTimer",
      "minimizeTimer",
      "setSelectedExerciseIndex", // NEU
    ]),
    restoreFeedbackSession() {
      // 0. Prüfen, ob wir im richtigen Context sind
      if (this.timerState.exerciseIndex !== null && this.timerState.exerciseIndex !== this.selectedExerciseIndex) {
        this.setSelectedExerciseIndex(this.timerState.exerciseIndex)
        // Superset SubIndex setzen? Vorerst nicht nötig, da completedSetsMap das regelt oder wir es global setzen müssten.
        // Falls nötig: this.setActiveSupersetSubIndex(...) aber das ist im Store.

        // Kurze Verzögerung, damit Vue den Index-Change verarbeiten kann?
        // Normalerweise reaktiv, aber sicherheitshalber:
        this.$nextTick(() => {
          this.restoreFeedbackSession()
        })
        return
      }

      // 1. Hole den letzten Satz der aktuellen Übung
      const sets = this.completedSetsMap[this.selectedExerciseIndex] || []
      if (sets.length === 0) return

      const lastSet = sets[sets.length - 1]

      // 2. Setze Status für Bearbeitung
      this.isEditingMinimisedSet = true
      this.pendingSet = JSON.parse(JSON.stringify(lastSet)) // Deep copy

      // 3. UI Status wiederherstellen
      this.showFeedbackOverlay = true
      this.feedbackState = "rating"

      // Versuche, vorherige Werte zu setzen
      const difficulty = lastSet.find((s) => s.text === "Difficulty")?.value
      if (difficulty) this.selectedDifficulty = difficulty

      const newWeight = lastSet.find((s) => s.text === "newWeight")?.value
      if (newWeight) this.adjustedWeight = newWeight

      // Timer läuft im Hintergrund weiter und wird durch Overlay angezeigt (isTimerActive ist true)
      this.isTimerActive = true
    },
    minimizeFeedback() {
      // 1. A. Timer-Werte retten
      const remainingContext = this.restTimerRemaining

      // 2. Globalen Timer starten
      if (remainingContext > 0 && !this.isEditingMinimisedSet) {
        let subId = null
        if (this.getSelectedExercise && this.getSelectedExercise.isSuperset) {
          subId = this.getActiveSupersetSubExercise?._id
        }

        this.startTimer({
          duration: remainingContext,
          startTime: Date.now() - (this.plannedRestDuration - remainingContext) * 1000,
          exerciseIndex: this.selectedExerciseIndex,
          subExerciseId: subId,
        })
        this.minimizeTimer() // Setzt isMinimized = true
      }

      // 3. Statt lokal zu speichern, nutzen wir die Hauptfunktion!
      // Diese kümmert sich um alles: Speichern, Weight Propagation, PR Check, Local Cleanup, etc.
      this.finishFeedbackAndCloseOverlay(false, true)
    },
    skipExercise(id) {
      if (!this.skippedExercises.includes(id)) {
        this.skippedExercises.push(id)
        if (this.$toast && this.$toast.info) {
          this.$toast.info("Übung für dieses Training ausgeblendet")
        }
      }
    },
    cleanupDragListeners() {
      window.removeEventListener("mousemove", this.dragMove)
      window.removeEventListener("touchmove", this.dragMove)
      window.removeEventListener("mouseup", this.dragEnd)
      window.removeEventListener("touchend", this.dragEnd)
    },
    transitionToNextHyroxExercise() {
      this.clearRestTimer() // Schliesst das Feedback-Overlay & setzt Timer zurück
      this.$nextTick(() => {
        if (this.nextExercise) {
          this.transitionData = {
            title: this.nextExercise.title,
            imageSrc: this.getImageForExercise(this.nextExercise),
          }
          this.showHyroxTransition = true

          // Nach 2.2 Sekunden zur nächsten Übung wechseln
          setTimeout(() => {
            this.showHyroxTransition = false
            this.open(this.nextExercise)
          }, 2200)
        }
      })
    },
    transitionToNextNormalExercise() {
      this.clearRestTimer() // Schliesst das Feedback-Overlay & setzt Timer zurück
      this.$nextTick(() => {
        if (this.nextExercise) {
          this.transitionData = {
            title: this.nextExercise.title,
            imageSrc: this.getImageForExercise(this.nextExercise),
          }
          this.transitionCountdown = 1.5
          this.showAutoTransition = true

          if (this.autoTransitionInterval) clearInterval(this.autoTransitionInterval)

          this.autoTransitionInterval = setInterval(() => {
            this.transitionCountdown = Math.max(0, this.transitionCountdown - 0.1)
            if (this.transitionCountdown <= 0) {
              clearInterval(this.autoTransitionInterval)
              this.showAutoTransition = false
              this.open(this.nextExercise)
            }
          }, 100)
        }
      })
    },
    cancelAutoTransition() {
      if (this.autoTransitionInterval) {
        clearInterval(this.autoTransitionInterval)
        this.autoTransitionInterval = null
      }
      this.showAutoTransition = false
    },

    getHyroxCategoryDetails(title) {
      if (!title) return { icon: null, text: "" }
      if (title.includes("Men Pro")) return { icon: "mdi-crown", text: "Men Pro" }
      if (title.includes("Women Pro")) return { icon: "mdi-crown-outline", text: "Women Pro" }
      if (title.includes("Men")) return { icon: "mdi-human-male", text: "Men" }
      if (title.includes("Women")) return { icon: "mdi-human-female", text: "Women" }
      if (title.includes("Üben")) return { icon: "mdi-dumbbell", text: "Freies Training" }
      return { icon: null, text: title }
    },
    redoWarmup() {
      if (this.hasWarmup && this.completedSetsArray.length === 1 && this.completedSetsArray[0].skipped) {
        this.removeCompletedSet({ idx: this.selectedExerciseIndex, setPos: 0 })
      }
    }, // In runWorkoutExerciseList.vue -> methods
    promptForWeightPropagation(weightForPropagation) {
      const ex = this.getSelectedExercise
      const exerciseIndex = this.selectedExerciseIndex

      let targetPlannedSets = []
      let completedCountForTarget = 0
      let subExerciseId = null

      // --- 1. Bestimmen: Wer bin ich und wie viele Sätze habe ich schon? ---
      if (ex.isSuperset) {
        // Wir schauen auf den zuletzt hinzugefügten Satz (this.finalSet ist der sicherste Weg)
        const lastSet = this.finalSet || this.completedSetsArray[this.completedSetsArray.length - 1]

        if (!lastSet || !lastSet.subExerciseId) {
          this.clearRestTimer()
          return
        }

        subExerciseId = lastSet.subExerciseId
        const subEx = ex.exercises.find((e) => e._id === subExerciseId)
        if (!subEx) return

        targetPlannedSets = subEx.sets || []
        // Zähle nur die fertigen Sätze DIESER Unter-Übung
        completedCountForTarget = this.completedSetsArray.filter((s) => s.subExerciseId === subExerciseId && !s.skipped).length
      } else {
        // Normale Übung
        targetPlannedSets = ex.sets || []
        completedCountForTarget = this.completedSetsArray.filter((s) => !s.isWarmup && !s.skipped).length
      }

      // --- 2. Prüfen: Sind wir schon fertig mit allen geplanten Sätzen? ---
      if (completedCountForTarget >= targetPlannedSets.length) {
        this.clearRestTimer()
        return
      }

      // --- 3. Abweichung berechnen ---
      // Der Index des gerade beendeten Satzes ist (Anzahl - 1)
      const justFinishedPlanIndex = completedCountForTarget - 1
      if (justFinishedPlanIndex < 0) return

      const plannedWeightOfCompletedSetVal = targetPlannedSets[justFinishedPlanIndex]?.find((s) => s.text === "Gewicht")?.value

      // Wenn kein Gewicht geplant war, können wir nichts vergleichen
      if (plannedWeightOfCompletedSetVal === null || plannedWeightOfCompletedSetVal === undefined) {
        this.clearRestTimer()
        return
      }

      const performedWeightNumber = Number(weightForPropagation)
      const plannedWeightNumber = Number(plannedWeightOfCompletedSetVal)
      const difference = performedWeightNumber - plannedWeightNumber

      // --- 4. Zukünftige Sätze prüfen ---
      const remainingSets = targetPlannedSets.slice(completedCountForTarget)

      const plannedWeightsOfRemainingSets = remainingSets.map((set) => Number(set.find((s) => s.text === "Gewicht")?.value)).filter((w) => !isNaN(w))

      if (plannedWeightsOfRemainingSets.length === 0) {
        this.clearRestTimer()
        return
      }

      // --- 5. Dialog vorbereiten ---
      const firstPlannedWeightOfRemaining = plannedWeightsOfRemainingSets[0]
      const areRemainingWeightsUniform = plannedWeightsOfRemainingSets.every((w) => w === firstPlannedWeightOfRemaining)
      const isIncrease = difference > 0

      // Payload für den Store vorbereiten (Hier ist der entscheidende Fix: subExerciseId!)
      const commonPayload = {
        exerciseIndex,
        subExerciseId,
        startingFromSetIndex: completedCountForTarget, // Ab dem nächsten (noch nicht gemachten) Satz
      }

      if (areRemainingWeightsUniform && firstPlannedWeightOfRemaining === plannedWeightNumber) {
        // Fall A: Absolute Anpassung (Alle zukünftigen Sätze auf den neuen Wert setzen)
        this.propagationData = {
          isIncrease: isIncrease,
          type: "absolute",
          oldValue: `${firstPlannedWeightOfRemaining} kg`,
          newValue: `${performedWeightNumber} kg`,
          description: "Soll das neue Gewicht für alle Folgesätze übernommen werden?",
        }
        this.propagationCallback = () =>
          this.updateFutureExerciseWeights({
            ...commonPayload,
            type: "absolute",
            value: performedWeightNumber,
          })
      } else {
        // Fall B: Relative Anpassung (Alle zukünftigen Sätze um die Differenz anpassen)
        const diffText =
          difference >= 0 ? `um ${Math.round(difference * 10) / 10} kg erhöht` : `um ${Math.abs(Math.round(difference * 10) / 10)} kg reduziert`

        const oldValuesForDisplay = plannedWeightsOfRemainingSets.slice(0, 3)
        const newValuesForDisplay = oldValuesForDisplay.map((w) => Math.max(0, w + difference))

        this.propagationData = {
          isIncrease: isIncrease,
          type: "relative",
          oldValue: oldValuesForDisplay,
          newValue: difference >= 0 ? `+${difference}` : `${difference}`,
          newValueCalculated: newValuesForDisplay,
          description: "Sollen die Gewichte der Folgesätze angepasst werden?",
          subDescription: `Die nächsten Sätze werden ${diffText}.`,
        }
        this.propagationCallback = () =>
          this.updateFutureExerciseWeights({
            ...commonPayload,
            type: "relative",
            value: difference,
          })
      }

      this.showWeightPropagationDialog = true
    },
    async checkAndTriggerPR(setToCheck) {
      try {
        if (this.activeSplit && !this.activeSplit.isHyrox) {
          const recordCheckResult = await this.checkForPersonalRecord({
            exercise: this.getSelectedExercise,
            completedSet: setToCheck,
          })

          if (recordCheckResult && recordCheckResult.isNewRecord) {
            this.prAnimationData = {
              recordType: recordCheckResult.type,
              newValue: recordCheckResult.newValue,
            }
            this.showPRAnimation = true
            this.triggerConfetti()
          }
        }
      } catch (error) {
        console.error("Fehler bei der Rekord-Prüfung:", error)
      }
    },
    executePropagation() {
      if (typeof this.propagationCallback === "function") {
        this.propagationCallback()
      }
      this.closePropagationDialog()
    },
    async closePropagationDialog() {
      this.showWeightPropagationDialog = false
      // Kurze Verzögerung, damit die Dialog-Animation sauber aussieht
      setTimeout(() => {
        this.propagationData = null
        this.propagationCallback = null
        // Wichtig: Erst hier den Timer-Overlay komplett schliessen
        this.clearRestTimer()
      }, 300)

      await this.checkAndTriggerPR(this.finalSet)
      if (this.activeSplit && this.activeSplit.isHyrox && this.nextExercise) {
        this.transitionToNextHyroxExercise()
      } else if (this.autoTransitionEnabled && this.isExerciseCompleted(this.getSelectedExercise) && this.nextExercise) {
        this.transitionToNextNormalExercise()
      } else {
        this.clearRestTimer()
      }
    },
    dragStart(e) {
      // Verhindert Drag, wenn andere Overlays aktiv sind
      if (
        this.showFeedbackOverlay ||
        this.exerciseDetailDialog ||
        this.showWeightPropagationDialog ||
        this.showIncompleteDialog ||
        this.showPRAnimation
      )
        return
      // Ignoriert Klicks, die nicht die linke Maustaste sind
      if (e.type === "mousedown" && e.button !== 0) return

      const pos = e.type === "touchstart" ? e.touches[0] : e

      // Ignoriere Gesten, die im untersten 50px-Bereich des Bildschirms starten,
      // um Konflikte mit iOS-Systemgesten (Home-Bar / Zurück-Wischen) zu vermeiden.
      if (pos.clientY > window.innerHeight - 50) return

      this.dragState.startX = pos.clientX
      this.dragState.currentX = pos.clientX // Wichtig: initialisieren
      this.dragState.startY = pos.clientY // Für die Richtungserkennung

      // Event Listeners hinzufügen
      window.addEventListener("mousemove", this.dragMove)
      window.addEventListener("touchmove", this.dragMove, { passive: false }) // passive:false, um scrollen zu verhindern
      window.addEventListener("mouseup", this.dragEnd)
      window.addEventListener("touchend", this.dragEnd)
    },
    dragMove(e) {
      // Sollte nicht passieren, aber sicher ist sicher
      if (this.dragState.startX === 0) return

      const pos = e.type === "touchmove" ? e.touches[0] : e
      const deltaX = pos.clientX - this.dragState.startX
      const deltaY = pos.clientY - this.dragState.startY

      // 1. Fall: Drag ist noch nicht "aktiv" (erste Bewegung)
      if (!this.dragState.active) {
        // Prüft, ob die Bewegung primär horizontal ist und einen Schwellwert überschreitet
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 15) {
          // Es ist ein horizontaler Swipe
          e.preventDefault() // Scrollen auf der Seite unterbinden

          const origin = deltaX > 0 ? "left" : "right" // left=von links, right=von rechts
          const avatarExercise = origin === "left" ? this.previousExercise : this.nextExercise

          if (!avatarExercise) {
            // Keine Übung in dieser Richtung, Drag abbrechen
            this.cleanupDragListeners()
            return
          }

          // Drag-Modus aktivieren
          this.dragState.active = true
          this.dragState.origin = origin
          this.dragState.avatarExercise = avatarExercise
          this.dragState.containerWidth = this.$el.clientWidth // Breite des Containers speichern
          this.dragState.dragThreshold = this.dragState.containerWidth * 0.3 // 30% Wischdistanz als Ziel
        } else if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 15) {
          // Es ist ein vertikaler Swipe (Scrollen), Drag abbrechen
          this.cleanupDragListeners()
        }
        // ansonsten: zu wenig Bewegung, auf nächstes Event warten
      }

      // 2. Fall: Drag ist bereits aktiv
      if (this.dragState.active) {
        e.preventDefault()
        this.dragState.currentX = pos.clientX

        const dragDistance = Math.abs(this.dragState.currentX - this.dragState.startX)
        this.dragState.dropZoneActive = dragDistance > this.dragState.dragThreshold
      }
    },

    dragEnd() {
      if (!this.dragState.active) {
        // Drag wurde nie gestartet (z.B. nur ein Klick oder vertikaler Swipe)
        this.cleanupDragListeners()
        return
      }

      if (this.dragState.dropZoneActive) {
        // Erfolg!
        this.triggerExerciseChange()
      } else {
        // Abgebrochen -> Avatar zurück animieren
        this.dragState.active = false // Triggert die "return" Animation im computed style
        this.resetDragState() // Räumt den State nach der Animation auf
      }

      this.cleanupDragListeners()
    },
    triggerExerciseChange() {
      this.dragState.isCompleting = true // Startet die "Success" Animation
      this.dragState.active = false

      // Warten bis die Animation (ca. 400ms) fertig ist
      setTimeout(() => {
        this.open(this.dragState.avatarExercise)
        this.resetDragState() // State komplett aufräumen
      }, 400)
    },

    resetDragState() {
      // Setzt active auf false (falls noch nicht geschehen)
      this.dragState.active = false
      this.dragState.dropZoneActive = false

      // Räumt den State nach der CSS-Transition (ca. 300ms) auf
      setTimeout(() => {
        this.dragState.origin = null
        this.dragState.startX = 0
        this.dragState.startY = 0
        this.dragState.currentX = 0
        this.dragState.avatarExercise = null
        this.dragState.isCompleting = false
        this.dragState.containerWidth = 0
      }, 300) // Muss zur CSS transition-duration passen
    },
    // AUTO-COMPLETE METHODS
    removeSet(exerciseIndex, setIndex) {
      if (this.autoCompletePreviewData[exerciseIndex]) {
        this.autoCompletePreviewData[exerciseIndex].sets.splice(setIndex, 1)
        // Wenn keine Sätze mehr da sind, entferne die ganze Übung
        if (this.autoCompletePreviewData[exerciseIndex].sets.length === 0) {
          this.removeExercise(exerciseIndex)
        }
      }
    },
    removeExercise(exerciseIndex) {
      this.autoCompletePreviewData.splice(exerciseIndex, 1)
    },
    getPlanForSetAtIndex(setIndex) {
      const mainExercise = this.getSelectedExercise
      if (!mainExercise || setIndex < 0) return []

      // Logik für Supersätze
      if (mainExercise.isSuperset) {
        // Finde heraus, welche Sub-Übung beim N-ten Satz des Supersatzes dran ist
        const subExIndex = setIndex % mainExercise.exercises.length
        const subEx = mainExercise.exercises[subExIndex]
        // Finde heraus, der wievielte Satz es für diese Sub-Übung ist
        const planIndexForSubEx = Math.floor(setIndex / mainExercise.exercises.length)
        return subEx.sets[planIndexForSubEx] || []
      }

      // Logik für Einzelübungen
      else {
        const hasWarmup = mainExercise.warmup && mainExercise.warmup.length > 0
        // Wenn es der erste Satz ist und ein Warmup existiert, nimm die Warmup-Vorgaben
        if (hasWarmup && setIndex === 0) {
          return mainExercise.warmup
        }
        // Ansonsten berechne den korrekten Index für die regulären Sätze
        const regularSetIndex = hasWarmup ? setIndex - 1 : setIndex
        if (regularSetIndex < 0) return []

        return mainExercise.sets[regularSetIndex] || []
      }
    },
    getCardStyle(exercise) {
      if (exercise.method && this.intensityTechniqueMapping[exercise.method]) {
        const color = this.intensityTechniqueMapping[exercise.method].color
        // Wir setzen eine CSS-Variable, die im Style-Block verwendet wird
        return { "--glow-color": color }
      }
      return {}
    },
    getCompletedSetsForSubExercise(superset, subExercise) {
      if (!superset || !subExercise) return []

      // Greife auf das Array aller Sätze für diesen Supersatz-Block zu
      // Wir verwenden die direkt gemappte `completedSetsMap`
      const allSetsForSuperset = this.completedSetsMap[superset.originalIndex] || []

      // Filtere die Sätze, die zur spezifischen Sub-Übung gehören,
      // indem du die 'subExerciseId' prüfst, die wir im Store hinzufügen.
      return allSetsForSuperset.filter((set) => set.subExerciseId === subExercise._id)
    },
    // NEUE METHODEN
    openSwapDialog() {
      this.selectionDialogMode = "swap"
      this.selectionDialogExerciseToSwap = this.getSelectedExercise
      this.isSelectionDialogActive = true
    },
    openAddDialog(index) {
      this.$emit("open-add-dialog", index)
    },
    handleSwapSelected({ newExercise, exerciseIndex }) {
      this.swapExerciseTemporarily({ newExercise, exerciseIndex })
    },
    handleAddSelected({ newExercise, atIndex }) {
      // Erstellt ein leeres Set-Array für die neue Übung
      const exerciseWithEmptySets = {
        ...newExercise,
        sets: [],
        warmup: null, // oder Standard-Warmup definieren falls gewünscht
      }
      this.addExerciseTemporarily({ newExercise: exerciseWithEmptySets, atIndex })

      // Optional: Direkt zur neu hinzugefügten Übung wechseln
      this.setSelectedExerciseIndex(atIndex)
    },
    promptFinishWorkout() {
      if (this.incompleteExercises.length > 0) {
        this.showIncompleteDialog = true
      } else {
        this.$router.push({ name: "WorkoutSummary" })
      }
    },

    finishAnyway() {
      this.showIncompleteDialog = false
      this.$router.push({ name: "WorkoutSummary" })
    },
    async promptAutoFinish() {
      // 1. Hole Vorschau-Daten vom Store
      const preview = await this.$store.dispatch("runWorkoutStore/generateAutoCompletionPreview")

      // 2. Setze lokale Daten (damit sie editiert werden können)
      // Wir brauchen kein Deep Copy hier, da generateAutoCompletionPreview schon neue Objekte liefert
      this.autoCompletePreviewData = preview

      this.showAutoFinishDialog = true
    },
    async confirmAutoFinish() {
      this.showAutoFinishDialog = false
      // 3. Sende die (editierten) Daten zurück zum Store
      this.$store.dispatch("runWorkoutStore/applyAutoCompletion", this.autoCompletePreviewData)

      // Kurzer Timeout für UX/Store-Update
      setTimeout(() => {
        this.$router.push({ name: "WorkoutSummary" })
      }, 300)
    },
    showInfoView() {
      this.feedbackState = "info"
    },
    hideInfoView() {
      this.feedbackState = "rating"
    },
    skipFeedback() {
      this.finishFeedbackAndCloseOverlay(true)
    },
    async finishFeedbackAndCloseOverlay(skipFeedback = false, forceCloseOverlayNow = false) {
      if (!this.pendingSet) return

      const performedWeight = this.pendingSet.find((s) => s.text === "Gewicht")?.value
      const suggestedNewWeight = this.adjustedWeight
      const deviationStatus = this.weightDeviationStatus

      let finalSet = [...this.pendingSet]

      if (!skipFeedback) {
        if (this.selectedDifficulty) {
          finalSet.push({ text: "Difficulty", value: this.selectedDifficulty })
        }
        // WICHTIG: Hier wird deine finale Entscheidung für das nächste Training gespeichert.
        if (suggestedNewWeight !== null) {
          finalSet.push({ text: "newWeight", value: suggestedNewWeight })
        }
        if (this.doNotShowAgain) {
          this.addDisabledFeedbackExercise(this.getSelectedExercise._id)
        }
      }

      if (this.isTimerActive && !this.isEditingMinimisedSet) {
        const actualDuration = this.pauseStartTime ? Math.round((Date.now() - this.pauseStartTime) / 1000) : 0
        finalSet.push({ text: "Tatsächliche Pause", value: actualDuration })
      }

      if (this.isRetrospective) {
        this.updateCompletedSet({
          exerciseIndex: this.selectedExerciseIndex,
          setIndex: this.retrospectiveSetIndex,
          updatedSet: finalSet,
        })
      } else if (this.isEditingMinimisedSet) {
        const sets = this.completedSetsMap[this.selectedExerciseIndex] || []
        const setIndex = sets.length - 1
        if (setIndex >= 0) {
          this.updateCompletedSet({
            exerciseIndex: this.selectedExerciseIndex,
            setIndex: setIndex,
            updatedSet: finalSet,
          })
        }
        this.minimizeTimer()
        this.isEditingMinimisedSet = false
      } else {
        this.addCompletedSet({
          set: finalSet,
          isWarmup: this.isAddingWarmupSet,
        })
      }

      // HIER IST DIE KORREKTUR: Die Logik prüft jetzt auf BEIDE Arten von Änderungen.
      const wasPerformedDifferently = deviationStatus === "higher" || deviationStatus === "lower"
      const wasAdjustedManually = suggestedNewWeight !== null && Number(suggestedNewWeight) !== Number(performedWeight)
      const shouldPromptForPropagation = performedWeight !== null && performedWeight !== undefined && (wasPerformedDifferently || wasAdjustedManually)

      if (shouldPromptForPropagation) {
        // Die 'weightForPropagation' ist IMMER deine finale Entscheidung (was du manuell gewählt hast),
        // ODER, falls du nichts gewählt hast, was du tatsächlich gehoben hast
        this.finalSet = finalSet
        const weightForPropagation = suggestedNewWeight !== null ? suggestedNewWeight : performedWeight

        // Wenn der User minimieren geklickt hat (Confirm Button), dann das Haupt-Overlay sofort ausblenden
        // damit das Propagierungs-Popup sauber drüber/allein liegt.
        if (forceCloseOverlayNow) {
          this.showFeedbackOverlay = false
        }

        this.$nextTick(() => {
          this.promptForWeightPropagation(weightForPropagation)
        })
      } else {
        await this.checkAndTriggerPR(finalSet)

        if (this.activeSplit && this.activeSplit.isHyrox && this.nextExercise) {
          this.transitionToNextHyroxExercise()
        } else if (this.autoTransitionEnabled && this.isExerciseCompleted(this.getSelectedExercise) && this.nextExercise) {
          this.transitionToNextNormalExercise()
        } else {
          this.clearRestTimer()
        }
        this.clearRestTimer()
      }

      this.$nextTick(async () => {
        const lastRow = this.$refs.dialogContent?.querySelector(".completed-table tbody tr:last-child")
        if (lastRow) {
          lastRow.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
      })
    },

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      // Fall 1: Das gehobene Gewicht weicht vom Plan ab (z.B. 85kg statt 80kg)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        const difference = deviation.actual - deviation.planned
        const tooltipData = {
          type: "adjustment",
          title: isIncrease ? "Gewichts-Steigerung" : "Gewichts-Anpassung",
          icon: isIncrease ? "🚀" : "🛠️",
          color: isIncrease ? "primary" : "orange",
          isIncrease: isIncrease,
          details: [
            { label: "Geplant", value: `${deviation.planned} kg` },
            { label: "Ausgeführt", value: `${deviation.actual} kg` },
            { label: "Differenz", value: `${difference > 0 ? "+" : ""}${difference} kg` },
          ],
        }
        // HIER IST DIE KORREKTUR: Prüfe, ob TROTZDEM eine andere Zukunfts-Planung gemacht wurde.
        if (newWeightEntry) {
          tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
        }
        return tooltipData
      }

      // Fall 2: Das gehobene Gewicht war korrekt, ABER es wurde eine Bewertung abgegeben.
      if (difficultyEntry) {
        let tooltipData
        switch (difficultyEntry.value) {
          case "hard":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Schwer",
              icon: "🥵",
              color: "#EF5350",
              details: "Du hast angegeben, dass dieser Satz extrem fordernd war und die letzten Wiederholungen kaum zu schaffen waren.",
            }
            break
          case "medium":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Perfekt",
              icon: "🙂",
              color: "#FFCA28",
              details: "Deine Bewertung zeigt, dass der Satz fordernd, aber mit guter Form machbar war. Genau richtig!",
            }
            break
          case "easy":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Leicht",
              icon: "😎",
              color: "#66BB6A",
              details: "Dieser Satz war einfach für dich. Du hättest deutlich mehr Wiederholungen oder Gewicht bewältigen können.",
            }
            break
        }
        // Auch hier prüfen wir, ob eine Zukunfts-Planung gemacht wurde.
        if (newWeightEntry) {
          tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
        }
        return tooltipData
      }

      // Fallback, falls heute noch keine Bewertung abgegeben wurde
      if (this.isTodaySelected) {
        return {
          type: "rating",
          title: "Feedback fehlt",
          icon: "💬",
          color: "grey",
          details: "Bewerte diesen Satz, um deinem Coach Feedback zu geben und deinen Plan zu optimieren.",
        }
      }

      return null
    },
    open(ex, ev) {
      this.historyViewIndex = 0
      this.setSelectedExerciseIndex(ex.originalIndex)
      this.slideIndex = 0
      this.isIntensityAlertExpanded = false
      this.clearRestTimer()
      this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(ex._id)
    },
    navigateHistory(direction) {
      const newIndex = this.historyViewIndex + direction
      if (newIndex >= 0 && newIndex <= this.historicalSessions.length) {
        this.historyViewIndex = newIndex
      }
    },
    prevSlide() {
      if (this.slideIndex > 0) this.slideIndex--
    },
    nextSlide() {
      if (this.slideIndex < this.totalSlides - 1) this.slideIndex++
    },

    handleAddSet(set) {
      if (!this.getSelectedExercise) return

      this.isAddingWarmupSet = this.hasWarmup && this.completedSetsArray.length === 0
      this.pendingSet = set

      if (this.slideIndex < this.totalSlides - 1) {
        this.slideIndex++
      }
      if (set.some((s) => s.skipped)) {
        this.finishFeedbackAndCloseOverlay(true)
      } else {
        this.triggerFeedbackFlow()
      }
    },
    onRemoveSet(idx) {
      if (this.hasWarmup && idx === 0) {
        const placeholder = { skipped: true, type: "warmup", id: Date.now(), showMenu: false }
        this.replaceCompletedSet({ idx: this.selectedExerciseIndex, setPos: 0, newSet: placeholder })
      } else {
        this.removeCompletedSet({ idx: this.selectedExerciseIndex, setPos: idx })
      }
    },
    promptDeleteSet(idx, set) {
      if (this.isWarmupBlocked && idx === 0) {
        alert("Du kannst das Warmup nicht mehr löschen, nachdem du mit dem Training begonnen hast.")
        return
      }
      if (this.hasWarmup && idx === 0) return
      if (set.skipped) return
      if (window.confirm(`Möchtest du den Satz ${this.hasWarmup ? (idx === 0 ? "Aufwärm" : this.realSetNumber(idx)) : idx + 1} wirklich löschen?`)) {
        this.onRemoveSet(idx)
      }
    },
    formatSetText(set) {
      if (!Array.isArray(set)) return ""
      if (set.skipped) {
        return "Warmup übersprungen (klicken zum Wiederholen)"
      }
      // Alle möglichen Werte aus dem Satz-Array extrahieren
      const distance = set.find((e) => e.text === "Distanz")?.value
      const weight = set.find((e) => e.text === "Gewicht")?.value
      const reps = set.find((e) => /Wiederholungen/.test(e.text))?.value
      const duration = set.find((e) => e.text === "Dauer")?.value

      // Fall 1: Es gibt eine gemessene Dauer.
      // Das deckt sowohl Ausdauer-Übungen als auch HYROX-Stationen ab.
      if (duration != null) {
        const totalSeconds = parseFloat(duration)
        if (!isNaN(totalSeconds)) {
          const minutes = Math.floor(totalSeconds / 60)
          const seconds = Math.floor(totalSeconds % 60)

          let timeString = ""
          if (minutes > 0) {
            timeString += `${minutes}min `
          }
          // Zeige Sekunden immer an, es sei denn, es sind genau 0 Minuten und 0 Sekunden.
          if (seconds > 0 || (minutes === 0 && totalSeconds >= 0)) {
            timeString += `${seconds}sek`
          }

          // Für HYROX-Übungen (oder andere, die zusätzliche Daten haben),
          // fügen wir diese in Klammern hinzu.
          const details = []
          if (weight) details.push(`${weight}kg`)
          if (reps) details.push(`${reps} Reps`)
          if (distance) details.push(`${distance}m`)

          if (details.length > 0) {
            timeString += ` (${details.join(" / ")})`
          }

          return timeString.trim()
        }
      }

      // Fall 2: Keine Dauer, also ein normales Training (Distanz, Gewicht, Reps)
      let mainText = ""
      if (distance) {
        mainText = `${distance}m`
        // Füge Gewicht nur hinzu, wenn es auch einen Wert hat
        if (weight != null && weight > 0) {
          mainText += ` x ${weight}kg`
        }
      } else if (weight != null && reps != null) {
        mainText = `${weight} kg x ${reps}`
      } else if (reps != null) {
        mainText = `${reps} Wdh.`
      }

      // Wenn kein Wert vorhanden ist, leeren String zurückgeben
      return mainText
    },
    getImageForExercise(ex) {
      if (ex.picture) return this.getImageUrl(ex.picture)
      if (ex.manual?.[0]?.picture) return this.getImageUrl(ex.manual[0].picture)
      return "/img/default-exercise.webp"
    },
    getImageUrl(id) {
      if (!id) return "/img/default-exercise.webp"
      let fn = String(id)
      if (fn.includes("/")) {
        fn = fn.substring(fn.lastIndexOf("/") + 1)
      }
      const nWoE = fn.replace(/\.[^/.]+$/, "")
      if (!nWoE) return "/img/default-exercise.webp"
      return `/img/uebungen/${id.replace(/\.[^/.]+$/, "")}.webp`
    },
    isExerciseCompleted(exercise) {
      if (!exercise) return false

      // Fallback falls plannedSetCount/completedSetCount nicht vorgeladen sind (für die Store-Übung)
      let originalIndex = exercise.originalIndex
      if (originalIndex === undefined && this.activeSplit?.exercises) {
        originalIndex = this.activeSplit.exercises.findIndex((ex) => ex._id === exercise._id)
      }

      if (originalIndex !== -1 && originalIndex !== undefined) {
        const allCompletedSets = this.completedSetsMap[originalIndex] || []
        const completedRegularCount = allCompletedSets.filter((s) => !s.isWarmup && !s.skipped).length

        if (exercise.title === "Senso Pro") {
          return completedRegularCount > 0
        }

        // Logic for Supersets
        if (exercise.isSuperset) {
          return exercise.exercises.every((subEx) => {
            const completedSets = this.getCompletedSetsForSubExercise(exercise, subEx)
            const plannedCount = subEx.sets ? subEx.sets.length : 0
            return completedSets.length >= plannedCount
          })
        }

        const plannedSetCount = exercise.sets?.length || 0
        if (plannedSetCount === 0 && exercise.warmup) {
          const warmupSet = allCompletedSets[0]
          return warmupSet && !warmupSet.skipped
        }

        return completedRegularCount >= plannedSetCount
      }

      if (exercise.title === "Senso Pro") {
        return exercise.completedSetCount > 0
      }

      if (exercise.isSuperset) {
        return exercise.exercises.every((subEx) => {
          const completedSets = this.getCompletedSetsForSubExercise(exercise, subEx)
          const plannedCount = subEx.sets ? subEx.sets.length : 0
          return completedSets.length >= plannedCount
        })
      }

      if (exercise.sets.length === 0 && exercise.warmup) {
        const warmupSet = (this.completedSetsMap[exercise.originalIndex] || [])[0]
        return warmupSet && !warmupSet.skipped
      } else if (exercise.plannedSetCount > 0) {
        return exercise.completedSetCount >= exercise.plannedSetCount
      }

      return false
    },
    toggleIntensityAlertExpansion() {
      this.isIntensityAlertExpanded = !this.isIntensityAlertExpanded
    },
    getSectionHeadlineColor(baseColor, isSecondary = false) {
      if (isSecondary) {
        return baseColor === "primary"
          ? "orange darken-1"
          : baseColor.includes("darken")
          ? baseColor.replace(/darken-\d/, "darken-3")
          : baseColor + " darken-2"
      }
      return baseColor.includes("lighten") ? baseColor.replace(/lighten-\d/, "") : baseColor.includes("darken") ? baseColor : baseColor + " darken-1"
    },
    getIconColorForItem(item, type) {
      if (type === "advantage") return "green accent-3"
      if (type === "reason") return "light-blue lighten-1"
      return "grey"
    },
    triggerConfetti() {
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors: ["#FFC107", "#FF9800", "#FFFFFF", "#FFD700"],
      })
    }, // In runWorkoutExerciseList.vue -> methods
    triggerFeedbackFlow() {
      // Bricht ab, wenn der User Feedback für diese Übung deaktiviert hat
      if ((this.disabledFeedbackExercises || []).includes(this.getSelectedExercise._id)) {
        this.finishFeedbackAndCloseOverlay(true)
        return
      }

      // NEU: Wenn ein globaler Timer läuft, stoppen wir ihn, da wir jetzt einen neuen Satz bearbeiten
      if (this.isGlobalTimerActive) {
        this.stopTimer()
      }

      // 2. Prüfe, ob eine Pause geplant ist
      const planForCompletedSet = this.getPlanForSetAtIndex(this.completedSetsArray.length)
      const pauseSetting = planForCompletedSet.find((s) => s.text === "Pause")
      const plannedPause = pauseSetting && pauseSetting.value && Number(pauseSetting.value) > 0 ? Number(pauseSetting.value) : 0

      // NEU: Zuerst Abweichung berechnen, damit wir ggf. trotz deaktiviertem Feedback die Propagierung triggern können
      this.weightDeviationStatus = "on_track"
      const plannedWeight = planForCompletedSet.find((s) => s.text === "Gewicht")?.value
      const actualWeight = this.pendingSet.find((s) => s.text === "Gewicht")?.value
      if (plannedWeight !== null && actualWeight !== undefined) {
        if (actualWeight > plannedWeight) this.weightDeviationStatus = "higher"
        else if (actualWeight < plannedWeight) this.weightDeviationStatus = "lower"
      }

      // NEU: Feedback deaktiviert? Dann direkt speiychern und Timer (falls aktiviert) minimiert starten
      // ABER: Wenn wir im Retrospective-Modus sind (manuelles Öffnen), dann ignorieren wir das!
      if (!this.feedbackEnabled && !this.isRetrospective) {
        if (this.timerEnabled) {
          // Timer starten (Count-Up wenn plannedPause == 0)
          this.startTimer({
            duration: plannedPause,
            startTime: Date.now(),
            exerciseIndex: this.selectedExerciseIndex, // Kontext für Wiederherstellung
            subExerciseId: this.getActiveSupersetSubExercise ? this.getActiveSupersetSubExercise._id : null,
          })
          this.minimizeTimer()
        }
        this.finishFeedbackAndCloseOverlay(true)
        return
      }

      // 2. Wenn es ein Hyrox-Wettkampf ist, zeige NUR den Timer (falls vorhanden)
      if (this.activeSplit.isHyrox && this.activeSplit.isCompetition) {
        if (plannedPause === 0) {
          // Kein Timer -> Direkt speichern und schliessen
          this.finishFeedbackAndCloseOverlay(true)
          return
        } else {
          // Nur den Timer anzeigen
          this.showFeedbackOverlay = true
          this.isTimerActive = true
          this.plannedRestDuration = plannedPause
          this.pauseStartTime = Date.now()
          this.clearRestTimerInterval()
          this.restTimerInterval = setInterval(this.updateTimerDisplay, 1000)
          this.updateTimerDisplay()
          this.feedbackState = "timer_only" // Neutraler Zustand ohne Smileys
          return // Wichtig: Beendet die Funktion hier
        }
      }

      // --- START: NEUE INTELLIGENZ ---
      // 1. Analysiere den Satz-Typ
      const hasDistance = this.pendingSet.some((s) => s.text === "Distanz")
      const hasWeight = this.pendingSet.some((s) => s.text === "Gewicht")
      const isRepsOnly = this.pendingSet.some((s) => /Wiederholungen/.test(s.text)) && !hasWeight && !hasDistance

      // Die Bewertung soll nur bei Gewichtsübungen erscheinen
      const shouldShowRating = hasWeight
      // --- ENDE: NEUE INTELLIGENZ ---

      // 3. Entscheide, was zu tun ist
      if (!shouldShowRating && plannedPause === 0) {
        // Fall A: Keine Bewertung und kein Timer -> Direkt speichern und schliessen
        this.finishFeedbackAndCloseOverlay(true)
        return
      }

      // Fall B: Entweder eine Bewertung oder ein Timer (oder beides) wird benötigt -> Overlay anzeigen
      this.showFeedbackOverlay = true

      if (this.timerEnabled && !this.isRetrospective) {
        this.isTimerActive = true
        this.plannedRestDuration = plannedPause
        this.pauseStartTime = Date.now()
        this.clearRestTimerInterval()
        this.restTimerInterval = setInterval(this.updateTimerDisplay, 1000)
        this.updateTimerDisplay()
      } else {
        this.isTimerActive = false
      }

      if (shouldShowRating) {
        // Normaler Flow mit Bewertung
        // Abweichung wurde bereits oben berechnet
        this.feedbackState = "rating"
      } else {
        // Flow nur mit Timer: Wir zeigen keine Smileys an
        this.feedbackState = "timer_only" // Ein neutraler Zustand
      }
    },

    updateTimerDisplay() {
      if (!this.pauseStartTime) return

      const elapsedSeconds = Math.floor((Date.now() - this.pauseStartTime) / 1000)
      const remaining = this.plannedRestDuration - elapsedSeconds

      if (remaining > 0) {
        this.restTimerRemaining = remaining
        this.overtimeSeconds = 0
      } else {
        this.restTimerRemaining = 0
        this.overtimeSeconds = -remaining // oder Math.abs(remaining)
      }
    },

    // NEU: Diese Methode wird aufgerufen, wenn der Tab wieder sichtbar wird.
    handleVisibilityChange() {
      if (document.visibilityState === "visible" && this.isTimerActive) {
        this.updateTimerDisplay()
      }
    },

    clearRestTimerInterval() {
      if (this.restTimerInterval) {
        clearInterval(this.restTimerInterval)
        this.restTimerInterval = null
      }
    },
    clearRestTimer() {
      this.clearRestTimerInterval()
      this.showFeedbackOverlay = false
      this.isTimerActive = false
      this.plannedRestDuration = 0
      this.restTimerRemaining = 0
      this.overtimeSeconds = 0
      this.pauseStartTime = null
      this.pendingSet = null
      this.feedbackState = "rating"
      this.selectedDifficulty = null
      this.weightSuggestions = []
      this.adjustedWeight = null
      this.doNotShowAgain = false
      this.isRetrospective = false
      this.retrospectiveSetIndex = null
      this.weightDeviationStatus = "on_track"
      this.isAddingWarmupSet = false
    },
    handleSmileyClick(difficulty) {
      this.selectedDifficulty = difficulty
      const hasWeight = this.pendingSet.some((p) => p.text === "Gewicht")

      if (this.weightDeviationStatus === "higher") {
        if (difficulty === "easy" || difficulty === "hard") {
          this.showWeightSuggestions()
        } else {
          // this.finishFeedbackAndCloseOverlay()
        }
        return
      }

      if (difficulty === "medium" || !hasWeight) {
        //this.finishFeedbackAndCloseOverlay()
      } else {
        this.showWeightSuggestions()
      }
    },
    showWeightSuggestions() {
      const currentWeightEntry = this.pendingSet.find((p) => p.text === "Gewicht")
      if (!currentWeightEntry || isNaN(parseFloat(currentWeightEntry.value))) {
        this.finishFeedbackAndCloseOverlay()
        return
      }
      const currentWeight = parseFloat(currentWeightEntry.value)
      let suggestions = []
      if (this.selectedDifficulty === "hard") {
        suggestions = [
          Math.round((currentWeight * 0.95) / 2.5) * 2.5,
          Math.round((currentWeight * 0.9) / 2.5) * 2.5,
          Math.round((currentWeight * 0.85) / 2.5) * 2.5,
        ]
      } else {
        suggestions = [currentWeight + 2.5, currentWeight + 5.0, currentWeight + 7.5]
      }
      this.weightSuggestions = [...new Set(suggestions.filter((w) => w > 0 && w < currentWeight * 2))].sort((a, b) => a - b).slice(0, 3)
      if (this.weightSuggestions.length === 0) {
        this.feedbackState = "rating"
        return
      }
      this.adjustedWeight = this.weightSuggestions[0]
      this.feedbackState = "show_weights"
    },
    getSmileyForSet(set) {
      if (!Array.isArray(set)) {
        return { icon: null, color: null }
      }
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      if (!difficultyEntry) return { icon: "mdi-help-circle-outline", color: "grey" }
      const difficulty = difficultyEntry.value
      switch (difficulty) {
        case "hard":
          return { icon: "mdi-emoticon-dead", color: "red" }
        case "medium":
          return { icon: "mdi-emoticon-happy", color: "orange" }
        case "easy":
          return { icon: "mdi-weight-lifter", color: "green" }
        default:
          return { icon: "mdi-help-circle-outline", color: "grey" }
      }
    },

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      let tooltipData = null

      // SCHRITT 1: Bestimme das primäre Feedback (Abweichung oder Bewertung)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        const difference = deviation.actual - deviation.planned
        tooltipData = {
          type: "adjustment",
          title: isIncrease ? "Gewichts-Steigerung" : "Gewichts-Anpassung",
          icon: isIncrease ? "🚀" : "🛠️",
          color: isIncrease ? "primary" : "orange",
          isIncrease: isIncrease,
          details: [
            { label: "Geplant", value: `${deviation.planned} kg` },
            { label: "Ausgeführt", value: `${deviation.actual} kg` },
            { label: "Differenz", value: `${difference > 0 ? "+" : ""}${difference} kg` },
          ],
        }
      } else if (difficultyEntry) {
        switch (difficultyEntry.value) {
          case "hard":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Schwer",
              icon: "🥵",
              color: "#EF5350",
              details: "Du hast angegeben, dass dieser Satz extrem fordernd war und die letzten Wiederholungen kaum zu schaffen waren.",
            }
            break
          case "medium":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Perfekt",
              icon: "🙂",
              color: "#FFCA28",
              details: "Deine Bewertung zeigt, dass der Satz fordernd, aber mit guter Form machbar war. Genau richtig!",
            }
            break
          case "easy":
            tooltipData = {
              type: "rating",
              title: "Bewertung: Zu Leicht",
              icon: "😎",
              color: "#66BB6A",
              details: "Dieser Satz war einfach für dich. Du hättest deutlich mehr Wiederholungen oder Gewicht bewältigen können.",
            }
            break
        }
      } else if (this.isTodaySelected) {
        return {
          type: "rating",
          title: "Feedback fehlt",
          icon: "💬",
          color: "grey",
          details: "Bewerte diesen Satz, um deinem Coach Feedback zu geben und deinen Plan zu optimieren.",
        }
      } else {
        return null // Kein Tooltip nötig, wenn es weder Abweichung noch Bewertung gibt.
      }

      // SCHRITT 2: Prüfe IMMER, ob eine Zukunfts-Anpassung existiert und füge sie hinzu.
      if (newWeightEntry) {
        tooltipData.newWeightSuggestion = `Neues Startgewicht: ${newWeightEntry.value} kg`
      }

      return tooltipData
    },
    openRatingTooltip(set) {
      this.completedSetsArray.forEach((s) => {
        if (s !== set) {
          this.$set(s, "showMenu", false)
        }
      })
      this.$set(set, "showMenu", !set.showMenu)
    },
    openRetrospectiveFeedback(set, setIndex) {
      this.pendingSet = set
      this.isRetrospective = true
      this.retrospectiveSetIndex = setIndex
      this.triggerFeedbackFlow()
    },
    // In runWorkoutExerciseList.vue -> methods
    editCurrentNote() {
      if (!this.currentSensoProSet) return
      this.editableNote = this.currentSensoProSet.find((s) => s.text === "note")?.value || ""
      this.editingNote = true
    },
    cancelEditNote() {
      this.editingNote = false
      this.editableNote = ""
    },
    saveCurrentNote() {
      const finalSet = [{ text: "note", value: this.editableNote }]
      // Wir nutzen die bestehende Action, um den Satz an Position 0 zu ersetzen
      this.replaceCompletedSet({
        idx: this.selectedExerciseIndex,
        setPos: 0,
        newSet: finalSet,
      })
      this.editingNote = false
    },
    getPlannedWeightForSet(setIndex) {
      if (!this.getSelectedExercise || setIndex < 0) return null

      const completedSet = this.completedSetsArray[setIndex]
      if (!completedSet) return null

      // --- NEUE Logik für Supersätze ---
      if (this.getSelectedExercise.isSuperset) {
        const subExercise = this.getSubExerciseForSet(completedSet)
        if (!subExercise || !subExercise.sets) return null

        // Zähle, der wievielte Satz es für DIESE Sub-Übung ist.
        const subSetIndex = this.completedSetsArray
          .slice(0, setIndex) // Nimm alle Sätze VOR dem aktuellen
          .filter((s) => s.subExerciseId === subExercise._id).length

        const plan = subExercise.sets[subSetIndex]
        return plan?.find((s) => s.text === "Gewicht")?.value || null
      }

      // --- Bestehende Logik für Einzelübungen, leicht verbessert ---
      const isThisWarmup = this.hasWarmup && completedSet.isWarmup

      if (isThisWarmup) {
        return this.getSelectedExercise.warmup?.find((s) => s.text === "Gewicht")?.value || null
      }

      // Berechne den korrekten Index für die regulären Sätze
      const regularSetsDoneBeforeThis = this.completedSetsArray.slice(0, setIndex).filter((s) => !s.isWarmup).length

      const plan = this.getSelectedExercise.sets[regularSetsDoneBeforeThis]
      return plan?.find((s) => s.text === "Gewicht")?.value || null
    },
    getWeightDeviation(set, setIndex) {
      if (!Array.isArray(set) || set.some((s) => s.skipped)) return { deviated: false }
      const plannedWeight = this.getPlannedWeightForSet(setIndex)
      const actualWeight = set.find((s) => s.text === "Gewicht")?.value

      if (plannedWeight === null || actualWeight === undefined || plannedWeight == actualWeight) {
        return { deviated: false }
      }

      return {
        deviated: true,
        planned: plannedWeight,
        actual: actualWeight,
      }
    },
    hasRating(set) {
      if (!Array.isArray(set)) return false
      return set.some((s) => s.text === "Difficulty")
    },
    getFeedbackIcon(set, idx) {
      const deviation = this.getWeightDeviation(set, idx)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        return { isEmoji: true, icon: isIncrease ? "🚀" : "🛠️" }
      }

      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      if (difficultyEntry) {
        switch (difficultyEntry.value) {
          case "hard":
            return { isEmoji: true, icon: "🥵" }
          case "medium":
            return { isEmoji: true, icon: "🙂" }
          case "easy":
            return { isEmoji: true, icon: "😎" }
        }
      }

      // Fallback für einen Satz, der heute gemacht wurde, aber noch kein Feedback hat
      if (this.isTodaySelected) {
        return { isEmoji: true, icon: "💬" }
      }

      return { isEmoji: false, icon: "mdi-minus", color: "grey" }
    },
    // in methods: { ... }

    getTooltipText(set, setIndex) {
      const deviation = this.getWeightDeviation(set, setIndex)
      const difficultyEntry = set.find((e) => e.text === "Difficulty")
      const newWeightEntry = set.find((e) => e.text === "newWeight")

      // Wenn es keinerlei Feedback gibt, wird kein Tooltip angezeigt.
      if (!deviation.deviated && !difficultyEntry && !newWeightEntry) {
        if (this.isTodaySelected) {
          return {
            type: "rating",
            title: "Feedback fehlt",
            icon: "💬",
            color: "grey",
            details: [{ label: "Bewerte diesen Satz, um deinen Plan zu optimieren." }],
          }
        }
        return null
      }

      // Wir bauen die Informationen für den Tooltip schrittweise auf.
      let title = "Feedback & Plan"
      let icon = "💡"
      let color = "primary"
      let details = []

      // Teil 1: Leistung (Was war geplant vs. was wurde gemacht?)
      if (deviation.deviated) {
        const isIncrease = deviation.actual > deviation.planned
        icon = isIncrease ? "🚀" : "🛠️"
        title = isIncrease ? "Starke Leistung!" : "Gewichts-Anpassung"
        color = isIncrease ? "primary" : "orange"
        details.push({ label: "Geplant", value: `${deviation.planned} kg` })
        details.push({ label: "Ausgeführt", value: `${deviation.actual} kg`, color: isIncrease ? "primary--text" : "orange--text" })
      }

      // Teil 2: Bewertung (Wie hat es sich angefühlt?)
      if (difficultyEntry) {
        const difficulties = {
          hard: { text: "Zu Schwer", icon: "🥵", color: "#EF5350" },
          medium: { text: "Perfekt", icon: "🙂", color: "#FFCA28" },
          easy: { text: "Zu Leicht", icon: "😎", color: "#66BB6A" },
        }
        const rating = difficulties[difficultyEntry.value]
        if (rating) {
          details.push({ label: "Deine Bewertung", value: rating.text, color: `${rating.color}--text` })
          if (!deviation.deviated) {
            // Wenn die Leistung wie geplant war, bestimmt die Bewertung den Titel.
            title = `Bewertung: ${rating.text}`
            icon = rating.icon
            color = rating.color
          }
        }
      }

      // Teil 3: Entscheidung (Was ist der Plan für die Zukunft?)
      if (newWeightEntry) {
        // Wenn eine Zukunfts-Entscheidung getroffen wurde, ist das die wichtigste Info.
        title = "Feedback & Plan-Update"
        icon = "🎯"
        color = "primary"
        details.push({ label: "Neues Startgewicht", value: `${newWeightEntry.value} kg`, color: "primary--text" })
      }

      return { title, icon, color, details }
    },
    // 5. NEUE Methode, die beim Klick auf das Feedback-Icon aufgerufen wird
    openFeedbackMenu(set, idx) {
      this.tooltipData = this.getTooltipText(set, idx)
    },
    updateSetRating(setIndex, difficulty) {
      let setToUpdate = [...this.completedSetsArray[setIndex]]
      const setInArray = this.completedSetsArray[setIndex]
      if (setInArray) this.$set(setInArray, "showMenu", false)
      setToUpdate = setToUpdate.filter((item) => item.text !== "Difficulty")
      setToUpdate.push({ text: "Difficulty", value: difficulty })
      this.updateCompletedSet({
        exerciseIndex: this.selectedExerciseIndex,
        setIndex: setIndex,
        updatedSet: setToUpdate,
      })
    },
    realSetNumber(idx) {
      if (!this.hasWarmup) return idx + 1
      let count = 0
      for (let i = 1; i <= idx; i++) {
        if (!this.completedSetsArray[i] || !this.completedSetsArray[i].skipped) {
          count++
        }
      }
      return count
    },
    formatTimeForDisplay(totalSeconds) {
      if (totalSeconds < 0) totalSeconds = 0
      return `${totalSeconds}s`
    },
    formatCountdownDisplay(currentSeconds, isOvertimeValue = false) {
      if (currentSeconds < 0) currentSeconds = 0
      const prefix = isOvertimeValue && currentSeconds > 0 ? "+" : ""
      return `${prefix}${currentSeconds}`
    },
    getSubExerciseForSet(set) {
      if (set && !set.subExerciseId && set.length) set = set[0]
      if (!this.getSelectedExercise?.isSuperset || !set.subExerciseId) return null
      return this.getSelectedExercise.exercises.find((ex) => ex._id === set.subExerciseId)
    },
    getSetNumber(set, overallIndex) {
      if (!this.getSelectedExercise.isSuperset) {
        // Alte Logik für Einzelübungen
        if (this.hasWarmup) return this.realSetNumber(overallIndex)
        return overallIndex + 1
      }
      // Neue Logik für Supersätze: Zähle nur die Sätze für DIESE Sub-Übung
      const setsForThisSubExercise = this.completedSetsArray.filter((s) => s.subExerciseId === set.subExerciseId)
      return setsForThisSubExercise.findIndex((s) => s.id === set.id || s === set) + 1
    },
    showImage(exercise) {
      if (!exercise) return
      this.detailDialogExercise = exercise
      this.exerciseDetailDialog = true
    },
  },
  watch: {
    getSelectedExercise(newVal, oldVal) {
      if (newVal && (!oldVal || newVal._id !== oldVal._id)) {
        this.slideIndex = 0
        this.isIntensityAlertExpanded = false
        this.disableFeedbackForExercise = (this.disabledFeedbackExercises || []).includes(newVal._id)
      }
      if (!newVal) {
        this.isIntensityAlertExpanded = false
        this.clearRestTimer()
      }
    },
  },
  mounted() {
    window.addEventListener("visibilitychange", this.handleVisibilityChange)
  },

  beforeDestroy() {
    this.clearRestTimerInterval()
    if (this.autoTransitionInterval) clearInterval(this.autoTransitionInterval)
    window.removeEventListener("visibilitychange", this.handleVisibilityChange)
  },
}
</script>
<style scoped>
.skipped-warmup-row {
  cursor: pointer;
  background-color: rgba(255, 165, 0, 0.1);
}

.skipped-warmup-row:hover {
  background-color: rgba(255, 165, 0, 0.2) !important;
}

/* ====== DIALOG & OVERLAY STYLES ====== */
.dialog-card.fullscreen {
  background-color: #121212 !important; /* Solid dark background for the main dialog */
  border-radius: 0;
  border: none !important;
  box-shadow: none !important;
}

.equipment-card,
.finished-sets-card {
  background: rgba(30, 30, 30, 0.6) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
}

.intensity-alert {
  background: rgba(20, 20, 20, 0.8) !important;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.intensity-alert-title {
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 0.9rem;
}

/* Table Styling */
.completed-table th {
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5) !important;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.completed-table td {
  font-size: 0.95rem;
  font-weight: 500;
  color: #eee;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

/* Carousel Styling */
.v-carousel {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

/* Input Section Wrapper */
.premium-input-card {
  background: linear-gradient(145deg, rgba(40, 40, 40, 0.4), rgba(20, 20, 20, 0.4));
  border-radius: 24px;
  padding: 4px; /* Border effect */
}
.glass-card-dialog {
  background: rgba(40, 40, 40, 0.7);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.superset-image-container {
  background: rgba(44, 44, 46, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px !important;
}
.superset-avatar-wrapper {
  transition: transform 0.3s ease-in-out;
  text-align: center;
}
.active-superset-exercise .v-avatar {
  transform: translateY(-10px);
  border: 4px solid orange;
  box-shadow: 0 0 10px orange !important;
}
.clickable-avatar {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.clickable-avatar:hover {
  transform: scale(1.2);
}
.tooltip-style {
  background-color: rgba(0, 0, 0, 0.8) !important;
  color: white !important;
}
/* ENDE NEUE KLASSEN */

/* ALTE, WIEDERHERGESTELLTE STILE */
.superset-label {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  color: orange;
  opacity: 0.8;
}
.feedback-tooltip-card {
  background-color: #2e2e2e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px !important;
  padding: 12px;
  color: white !important;
}
.feedback-tooltip-card .caption {
  color: rgba(255, 255, 255, 0.7) !important;
}
.caption-container {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
}
.caption-text {
  color: #fff;
  font-size: 0.9rem;
}
.rating-icon:hover,
.pr-activator .v-icon:hover {
  transform: scale(1.2);
  transition: transform 0.2s ease-in-out;
}
.exercise-list {
  background: linear-gradient(to bottom, #050505, #121212);
  min-height: 100vh;
}
.section-header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 85%;
  max-width: 450px;
  padding: 14px 20px;
  border-radius: 100px; /* Pill shape */
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(150%);
  -webkit-backdrop-filter: blur(20px) saturate(150%);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease;
  margin-bottom: 10px;
}

.section-header-content .section-title {
  font-size: 1rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: #ffffff;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

/* Spezifische Farben und Gradienten - Modernisiert */
.section-header-content.warmup {
  background: linear-gradient(135deg, rgba(255, 140, 0, 0.2), rgba(255, 69, 0, 0.2));
  border-color: rgba(255, 140, 0, 0.3);
}

.section-header-content.training {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border-color: rgba(255, 255, 255, 0.1);
}

.section-header-content.finish {
  background: linear-gradient(135deg, rgba(var(--v-primary-base-rgb), 0.4), rgba(var(--v-primary-base-rgb), 0.1));
  cursor: pointer;
  border-color: rgba(var(--v-primary-base-rgb), 0.4);
}

/* Interaktiver Effekt für den "Abschliessen"-Button */
.section-header-content.finish:hover {
  transform: scale(1.05);
  box-shadow: 0 0 30px rgba(var(--v-primary-base-rgb), 0.4);
}
.section-header {
  margin-bottom: 24px;
  margin-top: 12px;
  align-items: center;
  position: relative;
}
.section-title {
  color: #fff;
  font-size: 1rem;
  font-weight: bold;
  align-content: center;
}
.preset-label {
  font-size: 1.2rem;
  font-weight: 500;
  color: #fff;
}
/* 2030 GLASS CARD STYLE */
.card-inner {
  background: rgba(30, 32, 35, 0.4) !important;
  backdrop-filter: blur(24px) saturate(120%);
  -webkit-backdrop-filter: blur(24px) saturate(120%);
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  box-shadow: 0 8px 32px -8px rgba(0, 0, 0, 0.4) !important;
  width: 100%;
  padding: 16px;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow: hidden;
  position: relative;
  z-index: 1;
  margin-bottom: 4px; /* Slight spacing correction */
}

/* Subtle hover lift */
.exercise-card:hover .card-inner {
  transform: translateY(-3px);
  background: rgba(45, 48, 55, 0.5) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.5) !important;
}

.completed-checkmark {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  filter: drop-shadow(0 0 8px rgba(76, 175, 80, 0.6));
}

.exercise-card--warmup .card-inner,
.superset-card-inner {
  border-color: rgba(255, 165, 0, 0.3) !important;
  box-shadow: 0 10px 30px -10px rgba(255, 165, 0, 0.15) !important;
}

.exercise-card--warmup:hover .card-inner {
  box-shadow: 0 15px 40px -10px rgba(255, 165, 0, 0.25) !important;
}

/* Modern Completed State */
.exercise-card-inner--completed {
  background: linear-gradient(145deg, rgba(20, 40, 20, 0.4), rgba(30, 30, 30, 0.4)) !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  box-shadow: 0 0 30px -10px rgba(76, 175, 80, 0.2) !important;
}

.exercise-card-inner--completed .title {
  text-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
}

.card-img {
  object-fit: cover;
  margin-right: 16px;
  border-radius: 16px !important; /* Match card radius */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
}

.exercise-card:hover .card-img {
  transform: scale(1.05);
}
.title {
  color: #ffffff;
  font-size: 1.05rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  margin-bottom: 4px !important;
}
.intensity-chip {
  font-size: 0.7rem;
  height: auto;
  padding: 3px 8px;
  margin-top: 2px;
  margin-bottom: 2px;
  border-radius: 6px;
  font-weight: 600;
  letter-spacing: 0.05em;
}
.intensity-chip .v-icon {
  font-size: 0.9em;
}
.subtitle-2 {
  color: rgba(255, 255, 255, 0.6) !important;
  font-size: 0.85rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
}
.preset-chip {
  font-size: 0.9rem;
  margin-right: 8px;
  margin-bottom: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  letter-spacing: 0.03em;
}
.no-sets {
  color: #bbb;
  font-size: 1rem;
  padding: 8px 0;
}
.plan-settings {
  display: flex;
  flex-wrap: wrap;
  margin-top: 12px;
}
.finished-sets-card {
  margin-bottom: 16px;
}
.completed-table th,
.completed-table td {
  padding: 10px 12px;
  font-size: 0.95rem;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.completed-table tr:last-child td {
  border-bottom: none !important;
}
.completed-table th {
  color: #9e9e9e !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.orange--border {
  border: 2px solid orange !important;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}
.orange-dot {
  background-color: #ffa500 !important;
}
.overlay {
  position: fixed;
  inset: 0;
  background: #1e1e1e;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  z-index: 10;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.dialog-card.fullscreen {
  width: 100%;
  max-width: 100vw;
  height: 100%;
  overflow-y: auto;
  border-radius: 0;
}
.dialog-content {
  width: 100%;
}
.exercise-card {
  position: relative;
  width: calc(100% - 16px);
  margin-bottom: 16px;
}
/*
.exercise-card:not(:last-child)::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 100%;
  height: 16px;
  width: 2px;
  background: grey;
  z-index: 0;
  transition: background-color 0.3s ease;
}
  */
.exercise-card--warmup:not(:last-child)::after {
  background: orange !important;
}
.exercise-card.exercise-line-completed:not(.exercise-card--warmup):not(:last-child)::after {
  background: green;
}
.exercise-card--warmup.exercise-line-completed:not(:last-child)::after {
  background: green !important;
}
/*.section-header::after {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 100%;
  height: 16px;
  width: 2px;
  z-index: 0;
  transition: background-color 0.3s ease;
}
*/
.warmup-marker.section-header::after {
  background: orange;
}
.training-marker.section-header::after {
  background: grey;
}
::v-deep .v-list-item--link:before {
  background-color: transparent !important;
}
.table-container-transition-enter-active,
.table-container-transition-leave-active {
  transition: opacity 0.2s ease-out;
}
.table-container-transition-enter,
.table-container-transition-leave-to {
  opacity: 0;
}
.row-enter-active {
  transition: opacity 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.45s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.6s ease-out;
}
.row-leave-active {
  transition: opacity 0.35s cubic-bezier(0.75, 0, 0.25, 1), transform 0.35s cubic-bezier(0.75, 0, 0.25, 1),
    background-color 0.35s cubic-bezier(0.75, 0, 0.25, 1);
  position: absolute;
  width: 100%;
  left: 0;
  box-sizing: border-box;
  z-index: 1;
  background-color: rgba(217, 83, 79, 0.65);
  transform-origin: center center;
}
.row-enter {
  opacity: 0;
  transform: translateY(15px) scale(0.97);
  background-color: rgba(40, 167, 69, 0.3);
}
.row-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
  background-color: transparent;
}
.row-leave-to {
  opacity: 0;
  transform: scale(0.85);
  background-color: rgba(217, 83, 79, 0.3);
}
.completed-table tbody tr td {
  background-color: transparent !important;
}
.row-enter td,
.row-enter-to td,
.row-leave-active td,
.row-leave-to td {
  background-color: inherit !important;
}
.row-move {
  transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1);
}
.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.8);
  z-index: 2000;
  backdrop-filter: blur(8px);
}
.feedback-card {
  background-color: #2a2a2a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
  transition: max-height 0.4s ease-in-out;
  overflow: hidden;
  max-height: 100vh; /* Verhindert, dass die Karte zu gross wird */
  /* NEU: Animierter Hintergrund-Glow */
  background: radial-gradient(circle at 50% 0%, rgba(var(--v-primary-base-rgb), 0.15), transparent 70%);
  animation: bg-glow-pan 8s infinite alternate;
}
.feedback-section {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.status-title {
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
.countdown-text {
  font-size: 4rem;
  line-height: 1;
  font-weight: 900;
  font-family: "Roboto Condensed", "Roboto", sans-serif;
  letter-spacing: -0.02em;
}
.countdown-text-smaller {
  font-size: 3.5rem !important;
}
.completed-table tbody .clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}
.completed-table tbody .clickable-row:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.clickable-row td:last-child .v-icon {
  cursor: help;
}
.weight-suggestion-btn {
  flex-grow: 1;
  margin: 0 4px;
}
.rest-timer-overlay-fade-enter-active,
.rest-timer-overlay-fade-leave-active {
  transition: opacity 0.25s ease-in-out;
}
.rest-timer-overlay-fade-enter,
.rest-timer-overlay-fade-leave-to {
  opacity: 0;
}
.smiley-label {
  font-size: 0.75rem;
  margin-top: 4px;
  font-weight: 500;
}
.info-list .v-list-item-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  word-wrap: break-word !important;
}
.v-menu__content {
  z-index: 2001 !important;
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
@media (max-width: 380px) {
  .countdown-text {
    font-size: 3.5rem;
  }
  .countdown-text-smaller {
    font-size: 3rem !important;
  }
  .feedback-card {
    padding: 12px !important;
  }
  .text-h5.status-title {
    font-size: 1.3rem !important;
  }
}
.dialog-content::before {
  content: "";
  position: absolute;
  top: -1px; /* Korrigiert, um direkt am Rand zu sein */
  left: 0;
  right: 0;
  height: 30px;
  background: linear-gradient(to bottom, #1e1e1e, transparent);
  z-index: 5;
  pointer-events: none;
}

/* Spotlight-Effekt für die Übungsliste */
.exercise-card {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.is-inactive-exercise-focus {
  opacity: 0.4;
  transform: scale(0.95);
}
.is-active-exercise-focus {
  opacity: 1;
  transform: scale(1.02);
}

/* Verbesserte Typografie für die Vorgaben-Chips */
.preset-chip {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.preset-chip .v-icon {
  margin-right: 6px;
  color: rgba(255, 255, 255, 0.6);
}
.overlay {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.main-card-container {
  width: 100%;
  height: 100%;
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), filter 0.4s ease;
}

/* Binden der Progress-Farbe an eine CSS-Variable für den Glow-Effekt */
.v-progress-circular {
  filter: drop-shadow(0 0 8px var(--progress-color, rgba(0, 0, 0, 0.5)));
  transition: filter 0.5s ease-in-out;
}

/* NEU: Glow für lineare Progress-Bars */
.v-progress-linear__determinate {
  box-shadow: 0 0 8px currentColor;
}
.v-progress-linear {
  background: rgba(255, 255, 255, 0.05) !important;
  overflow: visible; /* Damit der Shadow nicht abgeschnitten wird */
}

/* Anpassung der Countdown-Schriftart für einen moderneren Look */
.countdown-text {
  font-size: 4.5rem; /* Etwas grösser */
  line-height: 1;
  font-weight: 900; /* Extra Fett */
  /* Eine moderne, komprimierte Schriftart wirkt oft gut */
  font-family: "Roboto Condensed", "Impact", sans-serif;
  letter-spacing: -0.04em; /* Buchstaben enger zusammen */
  transition: color 0.5s ease;
}

/* Animation für die letzten Sekunden */
.pulse-warning {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Animation für den "Fertig!" Text */
.status-title.final-state {
  animation: pulse-fade 0.5s ease-out;
}
.drag-avatar {
  position: fixed;
  width: 100px; /* Grössere Basis */
  height: 100px;
  border-radius: 50%;
  z-index: 20;
  pointer-events: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: 4px solid white; /* Dickerer Rand */

  /* Zentriert den Avatar in der Mitte des Viewports */
  top: 50%;
  left: 50%;
  /* Offset, damit (0,0) der Mittelpunkt ist */

  transform-origin: center center; /* Skaliert & bewegt sich vom Zentrum aus */

  /* Standard-Transition (für "zurück" oder "start") */
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;

  /* Startet unsichtbar und klein */
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.3); /* KORREKTUR: Korrekte Zentrierung */
}

/* "Ziel erreicht" Zustand */
.drag-avatar.on-target {
  border-color: #4caf50; /* Grüner Rand */
  box-shadow: 0 0 25px 5px rgba(76, 175, 80, 0.7); /* Grüner Glow */
}
@keyframes pulse-fade {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsivität für kleinere Bildschirme */
@media (max-width: 380px) {
  .countdown-text {
    font-size: 3.8rem;
  }
}
.celebration-title {
  /* Erzeugt einen schimmernden Regenbogen-Texteffekt */
  background: linear-gradient(90deg, #ff9a9e, #fad0c4, #fad0c4, #ff9a9e, #fbc2eb, #a18cd1, #fbc2eb, #ff9a9e);
  background-size: 200% auto;
  color: #000;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: celebrate-text-shine 4s linear infinite;
  font-weight: 900 !important;
}

@keyframes celebrate-text-shine {
  to {
    background-position: 200% center;
  }
}

.feedback-card.celebration-bg {
  /* Erzeugt einen pulsierenden, leuchtenden Hintergrund */
  animation: celebrate-bg-pulse 2s infinite ease-in-out;
}

@keyframes celebrate-bg-pulse {
  0% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 165, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 35px rgba(255, 215, 0, 0.7), 0 0 50px rgba(255, 165, 0, 0.5);
  }
  100% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 165, 0, 0.3);
  }
}

.glass-card-dialog {
  background: rgba(40, 40, 40, 0.8) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.weight-box {
  padding: 12px 8px;
  border-radius: 12px; /* Etwas runder */
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1.5rem;
  font-weight: 700;
  margin-top: 4px;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px; /* Mehr Abstand */
}

.new-weight.increase {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(102, 222, 132, 0.5);
  color: #a5d6a7;
  text-shadow: 0 0 8px rgba(102, 222, 132, 0.7);
}

.new-weight.decrease {
  background: rgba(251, 140, 0, 0.2);
  border-color: rgba(251, 140, 0, 0.5);
  color: #ffcc80;
  text-shadow: 0 0 8px rgba(251, 140, 0, 0.7);
}

.new-weight-value {
  opacity: 0;
  animation: new-weight-pop-in 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

@keyframes new-weight-pop-in {
  from {
    transform: scale(0.5) translateY(10px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* NEU: Styles für den vertikalen Flow-Indikator */
.connector-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px; /* Höhe des Bereichs */
  margin: -8px 0;
}
.connector-wrapper::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.1));
}

.operation-indicator {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.1rem;
  z-index: 1;
  border: 2px solid;
  animation: pulse-glow 2s infinite ease-in-out;
}
.operation-indicator .v-icon {
  font-size: 1.2rem;
  margin-bottom: -2px;
}
.operation-indicator.increase {
  background: radial-gradient(circle, rgba(76, 175, 80, 0.3) 0%, rgba(30, 30, 30, 0) 70%);
  border-color: rgba(102, 222, 132, 0.8);
  color: #a5d6a7;
}
.operation-indicator.decrease {
  background: radial-gradient(circle, rgba(251, 140, 0, 0.3) 0%, rgba(30, 30, 30, 0) 70%);
  border-color: rgba(251, 140, 0, 0.8);
  color: #ffcc80;
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 15px -5px currentColor;
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 25px 0px currentColor;
    transform: scale(1.05);
  }
}
.completed-table th {
  white-space: nowrap;
}

/* Style für die neuen Feedback-Emojis in der Tabelle */
.feedback-emoji {
  font-size: 1.5rem; /* Emojis grösser und auffälliger machen */
  line-height: 1;
  vertical-align: middle;
}

.feedback-activator {
  cursor: pointer;
  display: inline-block;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s ease-in-out;
}

.feedback-activator:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Völlig neues Design für den Tooltip */
.feedback-tooltip-card-v2 {
  min-width: 300px;
  border-radius: 16px !important;
  /* Glass-Effekt wird von deiner bestehenden Klasse übernommen */
}
.feedback-tooltip-card-v2 .v-card__title .title {
  font-size: 1.1rem !important;
  font-weight: bold;
}
.feedback-tooltip-card-v2 .v-card__text {
  font-size: 0.9rem;
  line-height: 1.5;
}

.rating-options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  width: 100%;
}

.rating-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.rating-option:hover {
  background: rgba(255, 255, 255, 0.1);
}

.rating-option.rating-hard:hover {
  border-color: #ef5350;
}
.rating-option.rating-medium:hover {
  border-color: #ffca28;
}
.rating-option.rating-easy:hover {
  border-color: #66bb6a;
}

.rating-emoji {
  font-size: 2.5rem;
  margin-right: 16px;
  line-height: 1;
}

.rating-text {
  text-align: left;
}

.rating-label {
  font-weight: bold;
  font-size: 1rem;
  color: white;
}

.rating-desc {
  font-size: 0.8rem;
  color: #bdbdbd;
}

.rating-info-emoji {
  font-size: 2rem;
  opacity: 0.9;
}

/* KORREKTUR FÜR INFO-ANSICHT (Stelle sicher, dass dies in deinem CSS ist) */
.info-list .v-list-item-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
}

@keyframes bg-glow-pan {
  from {
    background-position: 50% -50px;
  }
  to {
    background-position: 50% 100px;
  }
}

/* NEUE Struktur für Scrollbarkeit */
.feedback-card__header {
  flex-shrink: 0;
}
.feedback-card__content {
  flex-grow: 1;
  overflow-y: auto; /* HIER PASSIERT DIE MAGIE! */
  padding: 0 4px; /* Kleiner seitlicher Abstand für die Scrollleiste */
}
.feedback-card__footer {
  flex-shrink: 0;
}

/* NEU: Visuelles Feedback für ausgewählte Option */
.rating-option.is-selected {
  border-width: 2px;
}
.rating-option.rating-hard.is-selected {
  border-color: #ef5350;
  box-shadow: 0 0 15px #ef535066;
}
.rating-option.rating-medium.is-selected {
  border-color: #ffca28;
  box-shadow: 0 0 15px #ffca2866;
}
.rating-option.rating-easy.is-selected {
  border-color: #66bb6a;
  box-shadow: 0 0 15px #66bb6a66;
}

/* KORREKTUR FÜR INFO-ANSICHT (Robusterer CSS-Selektor) */
.info-list .info-subtitle {
  white-space: normal !important;
  line-height: 1.4 !important;
  overflow-wrap: break-word !important;
  height: auto !important; /* Verhindert, dass Vuetify die Höhe beschränkt */
  display: block !important;
}

/* NEU: Schönere Trennlinie */
.gradient-divider {
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
  height: 1px;
  border: none;
}

/* Fade-In Animation für das Overlay verbessern */
.rest-timer-overlay-fade-enter-active,
.rest-timer-overlay-fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.rest-timer-overlay-fade-enter-active .feedback-card,
.rest-timer-overlay-fade-leave-active .feedback-card {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.rest-timer-overlay-fade-enter,
.rest-timer-overlay-fade-leave-to {
  opacity: 0;
}
.rest-timer-overlay-fade-enter .feedback-card,
.rest-timer-overlay-fade-leave-to .feedback-card {
  transform: scale(0.95) translateY(20px);
}
.exercise-card-inner--completed-hyrox {
  box-shadow: 0 1px 6px #ffed00 !important;
  border: 2px solid #ffed00 !important;
}

.incomplete-dialog-card {
  border-radius: 24px !important;
  background: #1e1e1e !important;
  border: 1px solid rgba(251, 140, 0, 0.3);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(251, 140, 0, 0.2);
}

.incomplete-dialog-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(251, 140, 0, 0.15), transparent 70%);
  pointer-events: none;
}

.alert-icon {
  animation: alert-icon-pop 0.8s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  opacity: 0;
}

.incomplete-list .v-list-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

@keyframes alert-icon-pop {
  0% {
    transform: scale(0.6) rotate(-15deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.1) rotate(10deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}
/* NEU: Stile für den Hyrox-Übergang */
.hyrox-transition-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.85);
  z-index: 99999; /* Muss über dem Feedback-Overlay liegen */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.hyrox-transition-card {
  background-color: #2a2a2a !important;
  border: 1px solid #ffed00 !important;
  box-shadow: 0 8px 40px rgba(255, 237, 0, 0.3) !important;
  border-radius: 24px !important;
  width: 90%;
  max-width: 400px;
  animation: card-pop-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.transition-title {
  color: #ffed00;
  font-weight: 700;
  letter-spacing: 0.1em;
  font-size: 0.9rem;
  text-transform: uppercase;
}

@keyframes card-pop-in {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.note-display {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: flex-start; /* Stellt sicher, dass das Icon oben ist */
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.note-text {
  color: #e0e0e0; /* Helleres Grau für bessere Lesbarkeit */
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap; /* Erlaubt den automatischen Zeilenumbruch */
  word-break: break-word; /* Bricht auch lange Wörter um */
  flex-grow: 1;
}

/* ====== GLOBAL & BACKGROUND ====== */
.main-bg {
  background-color: #121212 !important;
  font-family: "Inter", sans-serif;
}

.exercise-scroll-area {
  overflow-y: auto;
  height: 100%;
  background: linear-gradient(180deg, #121212 0%, #0d0d0d 100%);
  padding-bottom: 80px !important;
}

/* ====== HEADERS ====== */
.section-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;
}
.header-text {
  font-size: 0.85rem;
  font-weight: 900;
  letter-spacing: 1.5px;
}

/* ====== CARD DESIGN SYSTEM (THE MODERN LOOK) ====== */
.exercise-wrapper {
  position: relative;
  transition: transform 0.2s ease;
}

.modern-card {
  /* Glass Effect */
  background: rgba(30, 30, 30, 0.7);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  /* Smooth Transition */
  transition: transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.3s, border-color 0.3s;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

/* Active Interaction */
.modern-card:active {
  transform: scale(0.98);
  background: rgba(255, 255, 255, 0.08);
}

/* Completed State */
.modern-card.is-completed {
  opacity: 0.8;
  background: rgba(20, 20, 20, 0.6);
  border-color: rgba(0, 150, 63, 0.3); /* Subtle green border for completed */
}
.modern-card.is-completed .exercise-title {
  color: #aaa;
}
.modern-card.is-completed .image-box {
  opacity: 0.6;
  filter: grayscale(50%);
}

/* Active State (Highlight) - THE BRAND RETURN */
.modern-card.is-active {
  background: rgba(45, 45, 45, 0.8);
  border: 1px solid #00963f; /* Primary Brand Color */
  box-shadow: 0 0 25px rgba(0, 150, 63, 0.25); /* Brand Glow */
}

/* ====== WARMUP INDICATOR ====== */
.warmup-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}
.warmup-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #ff9800;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}
.warmup-dot.filled {
  background: #ff9800;
  box-shadow: 0 0 10px rgba(255, 152, 0, 0.6);
}

/* ====== STATUS BORDER (Left Line) ====== */
.status-border {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #444; /* Default Pending */
  transition: background 0.3s ease;
}
.status-border.done {
  background: #00963f; /* Brand Green */
  box-shadow: 0 0 15px #00963f; /* Strong Glow */
}
.status-border.hyrox {
  background: #ffed00;
}

/* ====== CONTENT LAYOUT ====== */
.image-box {
  position: relative;
  width: 60px;
  height: 60px;
  flex-shrink: 0;
}
.image-box.small {
  width: 44px;
  height: 44px;
}

.exercise-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background-color: #1e1e1e;
  border-radius: 12px;
}

.completed-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 150, 63, 0.7); /* Brand Green Overlay */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

/* Typography */
.exercise-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: white;
  letter-spacing: 0.3px;
}
.exercise-title-small {
  font-weight: 600;
  font-size: 0.95rem;
  color: white;
}

/* Method Chips - NEW BRIGHT STYLE */
.method-chip {
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.2);
}

/* ====== PROGRESS PILLS ====== */
.progress-pills {
  display: flex;
  gap: 4px;
}
.progress-pills.small {
  gap: 3px;
}

.pill {
  width: 18px;
  height: 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}
.progress-pills.small .pill {
  width: 12px;
  height: 4px;
}

.pill.filled {
  background: #00963f; /* Brand Green */
  border-color: #00963f;
  box-shadow: 0 0 8px rgba(0, 150, 63, 0.6); /* Glow */
}
.pill.filled-warmup {
  background: #ff9800;
  border-color: #ff9800;
}
.pill.outlined-warmup {
  background: transparent;
  border-color: #ff9800;
}
.pill.filled-hyrox {
  background: #ffed00;
  border-color: #ffed00;
  box-shadow: 0 0 6px rgba(255, 237, 0, 0.4);
}

/* ====== SUPERSETS ====== */
.superset-container {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(40, 40, 40, 0.3);
}
/* The connecting line */
.superset-line-container {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.superset-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.1));
}
.superset-icon {
  position: relative;
  z-index: 2;
  background: #fff;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 10px white;
}
.superset-label {
  font-size: 0.65rem;
  letter-spacing: 2px;
  color: #ccc;
  font-weight: bold;
}

/* ====== BUTTONS ====== */
.finish-btn {
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(145deg, #333, #222) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.skip-btn {
  opacity: 0.5;
  transition: opacity 0.2s;
}
.skip-btn:hover {
  opacity: 1;
}

/* ====== DIALOGS CUSTOMIZATION ====== */
.glass-card-dialog {
  background: rgba(35, 35, 35, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}
.hyrox-transition-card {
  background: #111 !important;
  border: 2px solid #ffed00;
  box-shadow: 0 0 50px rgba(255, 237, 0, 0.3);
}
.feedback-card {
  background: #222;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Context Menu Styling */
.premium-context-menu {
  border-radius: 16px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden; /* Ensure rounded corners clip content */
}

.glass-menu-list {
  background: rgba(30, 30, 30, 0.95) !important;
  backdrop-filter: blur(20px);
}

.menu-item-rounded {
  border-radius: 8px !important;
  margin-bottom: 2px;
  transition: background 0.2s;
}

.menu-item-rounded:hover,
.menu-item-rounded:active {
  background: rgba(255, 255, 255, 0.08) !important;
}

/* Scrollbar adjustment for the list */
.exercise-scroll-area {
  overflow-y: auto;
  height: 100%;
  padding-bottom: 100px;
}
</style>

<style scoped>
/* NEU: Zentrierte Inputs für Auto-Complete */
.input-dense-centered input {
  text-align: center;
}
.input-dense-centered >>> .v-input__slot {
  padding: 0 8px !important;
}

/* NEU: Stile für den automatischen Übungswechsel */
.auto-transition-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 20, 20, 0.85);
  z-index: 99999;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.auto-transition-card {
  background-color: #2a2a2a !important;
  border: 1px solid #00e5ff !important;
  border-radius: 24px !important;
  width: 90%;
  max-width: 400px;
  animation: card-pop-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/* NEU: Stile für Auto-Complete Dialog */
.auto-finish-bg-glow {
  position: absolute;
  top: -10%;
  left: 20%;
  right: 20%;
  height: 300px;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}

.avatar-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-auto-input >>> input {
  text-align: center !important;
  color: white !important;
}

.custom-auto-input >>> .v-input__slot {
  border-radius: 8px !important;
}

.custom-auto-input >>> .v-input__append-inner {
  margin-top: 4px !important;
  opacity: 0.6;
}

.custom-auto-input >>> .v-text-field__suffix {
  opacity: 0.6;
  padding-left: 2px;
  font-size: 0.8rem;
  color: white !important;
}
</style>
